import QRCode from "qrcode";
import isNil from "lodash.isnil";
import {
  GetBattleByIdApiResponse as Battle,
  BattlesType,
  ShareImageType,
} from "@dripit/types";

import store from "@/store";
import fullLogoSvg from "@/assets/svg/fullLogo.svg";
import FullLogoImg from "@/assets/images/dripit-logo-lightblue.png";
import TrophyCircle from "@/assets/images/trophy-circle.png";
import { usdcToUsd } from "@/helpers";

// Helper functions
const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";

    img.onload = () => resolve(img);
    img.onerror = (error) => {
      reject(error);
    };
    img.src = src;
  });
};

const loadSvgFile = async (
  svgUrl: string,
  newFillColor?: string
): Promise<HTMLImageElement> => {
  const response = await fetch(svgUrl);
  let svgString = await response.text();

  if (newFillColor) {
    // Replace all fill attributes (except 'none') with the desired color
    svgString = svgString.replace(
      /fill="(?!none)[^"]*"/g,
      `fill="${newFillColor}"`
    );
    // If your SVG uses 'currentColor', you may want to also replace that:
    svgString = svgString.replace(
      /fill="currentColor"/g,
      `fill="${newFillColor}"`
    );
  }

  const svgBlob = new Blob([svgString], { type: "image/svg+xml" });
  const url = URL.createObjectURL(svgBlob);
  const img = new Image();
  return new Promise((resolve, reject) => {
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = reject;
    img.src = url;
  });
};

// Helper function to draw QR code and text
const drawQRCodeWithText = async (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  qrSize: number = 100
) => {
  const radius = 20;
  const refCode = store.getState()?.profile?.data?.refcode ?? null;
  const baseUrl = window.location.origin;
  const referralUrl = `${baseUrl}?refcode=${refCode}`;
  let qrImage: HTMLImageElement;

  if (isNil(refCode)) return;

  try {
    const qrDataUrl = await QRCode.toDataURL(referralUrl, {
      width: qrSize,
      margin: 2.5,
      color: { dark: "#000000", light: "#ffffff" },
    });

    // Create an image from the QR code data URL
    qrImage = new Image();
    qrImage.src = qrDataUrl;
    await new Promise((resolve) => {
      qrImage.onload = resolve;
    });
  } catch (err) {
    console.error("Error generating QR code:", err);
    return;
  }

  // Draw rounded rectangle background
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + qrSize - radius, y);
  ctx.quadraticCurveTo(x + qrSize, y, x + qrSize, y + radius);
  ctx.lineTo(x + qrSize, y + qrSize - radius);
  ctx.quadraticCurveTo(x + qrSize, y + qrSize, x + qrSize - radius, y + qrSize);
  ctx.lineTo(x + radius, y + qrSize);
  ctx.quadraticCurveTo(x, y + qrSize, x, y + qrSize - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();

  // Fill background
  ctx.fillStyle = "#ffffff";
  ctx.fill();

  // Draw the QR code
  ctx.drawImage(qrImage, x, y, qrSize, qrSize);

  // Add text below QR
  ctx.font = "18px GeistVF";
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("JOIN", x, y + qrSize + 12);
  ctx.fillText("THE ACTION", x, y + qrSize + 36);
};

export enum ShareInfoType {
  Position = "position",
  PositionSize = "positionSize",
  Winnings = "winnings",
  ReferralCode = "referralCode",
}

interface GenerateShareImageProps {
  battle: Battle;
  dimension: ShareImageType;
  shareInfo: ShareInfoType[];
}

export const generateHead2HeadShareImage = async ({
  battle,
  dimension,
  shareInfo,
}: GenerateShareImageProps) => {
  if (
    !battle ||
    !battle.battleSides ||
    !battle.battleSides[0]?.artist?.image ||
    !battle.battleSides[1]?.artist?.image ||
    !battle.battleSides[0]?.artist?.name ||
    !battle.battleSides[1]?.artist?.name
  )
    return;

  const showQR = shareInfo.includes(ShareInfoType.ReferralCode);
  const showPosition = shareInfo.includes(ShareInfoType.Position);
  const showPositionSize = shareInfo.includes(ShareInfoType.PositionSize);
  const showWinnings = shareInfo.includes(ShareInfoType.Winnings);
  const canvas = document.createElement("canvas");

  // Set dimensions based on ShareImageType
  if (dimension === ShareImageType.TWITTER) {
    canvas.width = 1200;
    canvas.height = 675;
  } else if (dimension === ShareImageType.INSTAGRAM_POST) {
    canvas.width = 1080;
    canvas.height = 1350;
  } else if (dimension === ShareImageType.INSTAGRAM_STORY) {
    canvas.width = 1080;
    canvas.height = 1920;
  } else {
    return; // Return if unsupported dimension type
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Background
  ctx.fillStyle = "#0F172A";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Load artist images
  const [artist1Image, artist2Image] = await Promise.all([
    loadImage(battle.battleSides[0].artist.image),
    loadImage(battle.battleSides[1].artist.image),
  ]);

  // Determine which artist was not selected
  const unselectedSideId =
    battle.userPositions?.length === 1
      ? battle.battleSides.find(
          (side) => side.id !== battle.userPositions[0].battle_side_id
        )?.id
      : null;

  if (dimension === ShareImageType.TWITTER) {
    // Twitter image layout
    // Draw artist images
    ctx.save();
    // Left artist with gradient path
    ctx.beginPath();
    ctx.moveTo(-19.8461, 0);
    ctx.lineTo(688.345, 0);
    ctx.lineTo(655.797, 351.413);
    ctx.lineTo(515.947, 329.952);
    ctx.lineTo(450.851, 676);
    ctx.lineTo(-19.8461, 676);
    ctx.closePath();
    ctx.strokeStyle = "white";
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.clip();

    // Draw image to match path width
    const leftPathWidth = 688.345 - -19.8461;
    ctx.drawImage(artist1Image, -19.8461, 0, leftPathWidth, canvas.height);

    // Add black and white effect with lighter opacity if this is the unselected artist
    if (unselectedSideId === battle.battleSides[0].id) {
      ctx.globalCompositeOperation = "color";
      ctx.fillStyle = "white";
      ctx.fillRect(-19.8461, 0, leftPathWidth, canvas.height);
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = "black";
      ctx.fillRect(-19.8461, 0, leftPathWidth, canvas.height);
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = "white";
      ctx.fillRect(-19.8461, 0, leftPathWidth, canvas.height);
      ctx.globalAlpha = 0.3; // Add slate-900 overlay
      ctx.fillStyle = "#0F172A"; // slate-900
      ctx.fillRect(-19.8461, 0, leftPathWidth, canvas.height);
      ctx.globalAlpha = 1;
    }

    // Add top gradient overlay
    const topGradient1 = ctx.createLinearGradient(0, 0, 0, canvas.height);
    topGradient1.addColorStop(0, "rgba(15, 23, 42, 0)");
    topGradient1.addColorStop(0.8299, "#0F172A");
    ctx.fillStyle = topGradient1;
    ctx.fillRect(-19.8461, 0, leftPathWidth, canvas.height);

    // Left artist name background
    const hasLeftPosition =
      showPosition &&
      battle.userPositions?.some(
        (pos) => pos.battle_side_id === battle.battleSides[0].id
      );
    const leftPositionSize =
      showPositionSize &&
      battle.userPositions?.find(
        (pos) => pos.battle_side_id === battle.battleSides[0].id
      )?.amount;

    // Check if user won on left side
    const leftSideWon =
      battle.battleSides[0].amount > battle.battleSides[1].amount;
    const isLeftWinner =
      hasLeftPosition && leftSideWon && battle.winner_id !== null;

    if (isLeftWinner) {
      // Create gradient for winner
      const gradient = ctx.createLinearGradient(50, 504, 50 + 500, 504);
      gradient.addColorStop(0, "#45E2C0");
      gradient.addColorStop(1, "#C1FFE4");
      ctx.fillStyle = gradient;
    } else {
      ctx.fillStyle = hasLeftPosition ? "#FFFFFF" : "rgba(255, 255, 255, 0.3)";
    }

    const leftX = 50;
    const leftY = 504;
    const leftWidth = 500;
    const leftHeight = 107; // Keep constant height
    const radius = 19;

    ctx.beginPath();
    ctx.moveTo(leftX + radius, leftY);
    ctx.lineTo(leftX + leftWidth - radius, leftY);
    ctx.quadraticCurveTo(
      leftX + leftWidth,
      leftY,
      leftX + leftWidth,
      leftY + radius
    );
    ctx.lineTo(leftX + leftWidth, leftY + leftHeight - radius);
    ctx.quadraticCurveTo(
      leftX + leftWidth,
      leftY + leftHeight,
      leftX + leftWidth - radius,
      leftY + leftHeight
    );
    ctx.lineTo(leftX + radius, leftY + leftHeight);
    ctx.quadraticCurveTo(
      leftX,
      leftY + leftHeight,
      leftX,
      leftY + leftHeight - radius
    );
    ctx.lineTo(leftX, leftY + radius);
    ctx.quadraticCurveTo(leftX, leftY, leftX + radius, leftY);
    ctx.closePath();
    ctx.fill();

    // Left artist name
    ctx.font = "40px PhonkRegular";
    ctx.fillStyle = hasLeftPosition ? "#0F172A" : "white";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(
      battle.battleSides[0].artist.name,
      leftX + 16,
      leftY + (showPositionSize && leftPositionSize ? 35 : leftHeight / 2)
    );

    // Left position size
    if (showPositionSize && leftPositionSize) {
      ctx.font = "800 32px GeistVF";
      ctx.fillStyle = hasLeftPosition ? "#0F172A" : "white";
      const text = `$ ${usdcToUsd(Number(leftPositionSize)).toFixed(2)}`;
      const textWidth = ctx.measureText(text).width;

      // Check if left side won
      const leftSideWon =
        battle.battleSides[0].amount > battle.battleSides[1].amount;
      const isLeftWinner =
        hasLeftPosition && leftSideWon && battle.winner_id !== null;

      // Draw text
      ctx.fillText(text, leftX + 16, leftY + leftHeight - 20);

      // Draw trophy if artist won and battle is resolved
      if (isLeftWinner) {
        const trophyImage = await loadImage(TrophyCircle);
        const trophySize = 48;
        ctx.drawImage(
          trophyImage,
          leftX + 200 + textWidth + 48,
          leftY + leftHeight - 25 - trophySize / 2,
          trophySize,
          trophySize
        );
      }
    }

    // Draw winning amount and profit percentage above the box if showWinnings is true and user won
    if (showWinnings) {
      const leftSideWon =
        battle.battleSides[0].amount > battle.battleSides[1].amount;
      const isLeftWinner =
        hasLeftPosition && leftSideWon && battle.winner_id !== null;

      if (isLeftWinner) {
        const claim = battle.claims?.find(
          (claim) => claim.position_id === battle.userPositions?.[0]?.id
        );
        if (claim) {
          const winningAmount = usdcToUsd(Number(claim.amount));
          const positionSize = battle.userPositions?.find(
            (pos) => pos.battle_side_id === battle.battleSides[0].id
          )?.amount;
          const profitPercentage = positionSize
            ? (winningAmount / usdcToUsd(Number(positionSize)) - 1) * 100
            : 0;

          // Draw label background
          // 10% larger for Instagram post and story
          let labelPadding = 16;
          let labelSpacing = 8;
          let labelHeight = 56;
          let labelRadius = 24; // Increased radius for all dimensions
          let winningFont = "800 32px PhonkRegular";
          let profitFont = "600 24px GeistVF";
          let labelY = leftY - 72;
          if (
            (dimension as ShareImageType) === ShareImageType.INSTAGRAM_POST ||
            (dimension as ShareImageType) === ShareImageType.INSTAGRAM_STORY
          ) {
            labelPadding = Math.round(labelPadding * 1.1);
            labelSpacing = Math.round(labelSpacing * 1.1);
            labelHeight = Math.round(labelHeight * 1.1);
            labelRadius = Math.round(labelRadius * 1.1);
            winningFont = "800 35px PhonkRegular"; // 32 * 1.1 ≈ 35
            profitFont = "600 26px GeistVF"; // 24 * 1.1 ≈ 26
            labelY = Math.round(labelY - labelHeight * 0.05); // Move label slightly higher for larger label
          }

          // Calculate text widths
          ctx.font = winningFont;
          const winningText = `$ ${winningAmount.toFixed(2)}`;
          const winningWidth = ctx.measureText(winningText).width;

          ctx.font = profitFont;
          const profitText = `+${profitPercentage.toFixed(1)}%`;
          const profitWidth = ctx.measureText(profitText).width;

          const totalWidth =
            winningWidth + profitWidth + labelPadding * 2 + labelSpacing;
          const labelX = leftSideWon
            ? leftX + 4
            : leftX + 16 + totalWidth - totalWidth + 12;

          // Draw rounded rectangle background
          ctx.beginPath();
          ctx.moveTo(labelX + labelRadius, labelY);
          ctx.lineTo(labelX + totalWidth - labelRadius, labelY);
          ctx.quadraticCurveTo(
            labelX + totalWidth,
            labelY,
            labelX + totalWidth,
            labelY + labelRadius
          );
          ctx.lineTo(labelX + totalWidth, labelY + labelHeight - labelRadius);
          ctx.quadraticCurveTo(
            labelX + totalWidth,
            labelY + labelHeight,
            labelX + totalWidth - labelRadius,
            labelY + labelHeight
          );
          ctx.lineTo(labelX + labelRadius, labelY + labelHeight);
          ctx.quadraticCurveTo(
            labelX,
            labelY + labelHeight,
            labelX,
            labelY + labelHeight - labelRadius
          );
          ctx.lineTo(labelX, labelY + labelRadius);
          ctx.quadraticCurveTo(labelX, labelY, labelX + labelRadius, labelY);
          ctx.closePath();

          // Fill background
          ctx.fillStyle = "#0F172A";
          ctx.fill();

          // Draw border
          ctx.strokeStyle = "#A2ECD1";
          ctx.lineWidth = 2;
          ctx.stroke();

          // Draw winning amount
          ctx.font = winningFont;
          ctx.fillStyle = "#A2ECD1";
          ctx.textAlign = "left";
          ctx.fillText(
            winningText,
            labelX + labelPadding,
            labelY + labelHeight / 2 + 2
          );

          // Draw profit percentage
          ctx.font = profitFont;
          ctx.fillStyle = "#4ADE80";
          ctx.fillText(
            profitText,
            labelX + labelPadding + winningWidth + labelSpacing,
            labelY + labelHeight / 2 + 2
          );
        }
      }
    }
    ctx.restore();

    // Right artist with gradient path
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(1200, 676);
    ctx.lineTo(476, 676);
    ctx.lineTo(541.5676, 372.873);
    ctx.lineTo(681.417, 394.333);
    ctx.lineTo(713.965, 0);
    ctx.lineTo(1200, 0);
    ctx.closePath();
    ctx.strokeStyle = "white";
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.clip();

    // Draw image to match path width
    const pathWidth = 1200 - 476;
    ctx.drawImage(artist2Image, 476, 0, pathWidth, canvas.height);

    // Add black and white effect with lighter opacity if this is the unselected artist
    if (unselectedSideId === battle.battleSides[1].id) {
      ctx.globalCompositeOperation = "color";
      ctx.fillStyle = "white";
      ctx.fillRect(476, 0, pathWidth, canvas.height);
      ctx.globalCompositeOperation = "source-over";
      ctx.globalAlpha = 0.3;
      ctx.fillStyle = "black";
      ctx.fillRect(476, 0, pathWidth, canvas.height);
      ctx.globalAlpha = 0.2;
      ctx.fillStyle = "white";
      ctx.fillRect(476, 0, pathWidth, canvas.height);
      ctx.globalAlpha = 0.3; // Add slate-900 overlay
      ctx.fillStyle = "#0F172A"; // slate-900
      ctx.fillRect(476, 0, pathWidth, canvas.height);
      ctx.globalAlpha = 1;
    }

    // Add top gradient overlay
    const topGradient2 = ctx.createLinearGradient(0, 0, 0, canvas.height);
    topGradient2.addColorStop(0, "rgba(15, 23, 42, 0)");
    topGradient2.addColorStop(0.8299, "#0F172A");
    ctx.fillStyle = topGradient2;
    ctx.fillRect(476, 0, pathWidth, canvas.height);

    // Right artist name background
    const hasRightPosition =
      showPosition &&
      battle.userPositions?.some(
        (pos) => pos.battle_side_id === battle.battleSides[1].id
      );
    const rightPositionSize =
      showPositionSize &&
      battle.userPositions?.find(
        (pos) => pos.battle_side_id === battle.battleSides[1].id
      )?.amount;

    // Check if user won on right side
    const rightSideWon =
      battle.battleSides[1].amount > battle.battleSides[0].amount;
    const isRightWinner =
      hasRightPosition && rightSideWon && battle.winner_id !== null;

    if (isRightWinner) {
      // Create gradient for winner
      const gradient = ctx.createLinearGradient(476, 504, 476 + 486, 504);
      gradient.addColorStop(0, "#45E2C0");
      gradient.addColorStop(1, "#C1FFE4");
      ctx.fillStyle = gradient;
    } else {
      ctx.fillStyle = hasRightPosition
        ? "#FFFFFF"
        : "rgba(255, 255, 255, 0.25)";
    }

    const rightX = 476;
    const rightY = 504;
    const rightWidth = 486;
    const rightHeight = 107; // Keep constant height

    ctx.beginPath();
    ctx.moveTo(rightX, rightY);
    ctx.lineTo(rightX + 466.12, rightY);
    ctx.bezierCurveTo(
      rightX + 478.012,
      rightY,
      rightX + 486.981,
      rightY + 10.8011,
      rightX + 484.796,
      rightY + 22.4909
    );
    ctx.lineTo(rightX + 471.899, rightY + 91.4909);
    ctx.bezierCurveTo(
      rightX + 470.218,
      rightY + 100.482,
      rightX + 462.369,
      rightY + rightHeight,
      rightX + 453.222,
      rightY + rightHeight
    );
    ctx.lineTo(rightX, rightY + rightHeight);
    ctx.closePath();
    ctx.fill();

    // Right artist name
    ctx.font = "44px PhonkRegular";
    ctx.fillStyle = hasRightPosition ? "#0F172A" : "white";
    ctx.textAlign = "left";
    ctx.textBaseline = "middle";
    ctx.fillText(
      battle.battleSides[1].artist.name,
      rightX + 56,
      rightY + (showPositionSize && rightPositionSize ? 43 : rightHeight / 2)
    );

    // Right position size
    if (showPositionSize && rightPositionSize) {
      ctx.font = "800 32px GeistVF";
      ctx.fillStyle = hasRightPosition ? "#0F172A" : "white";
      const text = `$ ${usdcToUsd(Number(rightPositionSize)).toFixed(2)}`;
      const textWidth = ctx.measureText(text).width;

      // Check if right side won
      const rightSideWon =
        battle.battleSides[1].amount > battle.battleSides[0].amount;
      const isRightWinner =
        hasRightPosition && rightSideWon && battle.winner_id !== null;

      // Draw text
      ctx.fillText(text, rightX + 56, rightY + rightHeight - 20);

      // Draw trophy if artist won and battle is resolved
      if (isRightWinner) {
        const trophyImage = await loadImage(TrophyCircle);
        const trophySize = 48;
        ctx.drawImage(
          trophyImage,
          rightX + 240 + textWidth + 48,
          rightY + rightHeight - 25 - trophySize / 2,
          trophySize,
          trophySize
        );
      }
    }

    // Draw winning amount and profit percentage above the box if showWinnings is true and user won
    if (showWinnings) {
      const rightSideWon =
        battle.battleSides[1].amount > battle.battleSides[0].amount;
      const isRightWinner =
        hasRightPosition && rightSideWon && battle.winner_id !== null;

      if (isRightWinner) {
        const claim = battle.claims?.find(
          (claim) => claim.position_id === battle.userPositions?.[0]?.id
        );
        if (claim) {
          const winningAmount = usdcToUsd(Number(claim.amount));
          const positionSize = battle.userPositions?.find(
            (pos) => pos.battle_side_id === battle.battleSides[1].id
          )?.amount;
          const profitPercentage = positionSize
            ? (winningAmount / usdcToUsd(Number(positionSize)) - 1) * 100
            : 0;

          // Draw label background
          // 10% larger for Instagram post and story
          let labelPadding = 16;
          let labelSpacing = 8;
          let labelHeight = 56;
          let labelRadius = 24; // Increased radius for all dimensions
          let winningFont = "800 32px PhonkRegular";
          let profitFont = "600 24px GeistVF";
          let labelY = rightY - 72;
          if (
            (dimension as ShareImageType) === ShareImageType.INSTAGRAM_POST ||
            (dimension as ShareImageType) === ShareImageType.INSTAGRAM_STORY
          ) {
            labelPadding = Math.round(labelPadding * 1.1);
            labelSpacing = Math.round(labelSpacing * 1.1);
            labelHeight = Math.round(labelHeight * 1.1);
            labelRadius = Math.round(labelRadius * 1.1);
            winningFont = "800 35px PhonkRegular"; // 32 * 1.1 ≈ 35
            profitFont = "600 26px GeistVF"; // 24 * 1.1 ≈ 26
            labelY = Math.round(labelY - labelHeight * 0.05); // Move label slightly higher for larger label
          }

          // Calculate text widths
          ctx.font = winningFont;
          const winningText = `$ ${winningAmount.toFixed(2)}`;
          const winningWidth = ctx.measureText(winningText).width;

          ctx.font = profitFont;
          const profitText = `+${profitPercentage.toFixed(1)}%`;
          const profitWidth = ctx.measureText(profitText).width;

          const totalWidth =
            winningWidth + profitWidth + labelPadding * 2 + labelSpacing;
          const labelX = rightSideWon
            ? rightX +
              4 +
              ((dimension as ShareImageType) === ShareImageType.TWITTER
                ? 230
                : 0) // Add 100px only for Twitter
            : rightX + 16 + totalWidth - totalWidth + 12;

          // Draw rounded rectangle background
          ctx.beginPath();
          ctx.moveTo(labelX + labelRadius, labelY);
          ctx.lineTo(labelX + totalWidth - labelRadius, labelY);
          ctx.quadraticCurveTo(
            labelX + totalWidth,
            labelY,
            labelX + totalWidth,
            labelY + labelRadius
          );
          ctx.lineTo(labelX + totalWidth, labelY + labelHeight - labelRadius);
          ctx.quadraticCurveTo(
            labelX + totalWidth,
            labelY + labelHeight,
            labelX + totalWidth - labelRadius,
            labelY + labelHeight
          );
          ctx.lineTo(labelX + labelRadius, labelY + labelHeight);
          ctx.quadraticCurveTo(
            labelX,
            labelY + labelHeight,
            labelX,
            labelY + labelHeight - labelRadius
          );
          ctx.lineTo(labelX, labelY + labelRadius);
          ctx.quadraticCurveTo(labelX, labelY, labelX + labelRadius, labelY);
          ctx.closePath();

          // Fill background
          ctx.fillStyle = "#0F172A";
          ctx.fill();

          // Draw border
          ctx.strokeStyle = "#A2ECD1";
          ctx.lineWidth = 2;
          ctx.stroke();

          // Draw winning amount
          ctx.font = winningFont;
          ctx.fillStyle = "#A2ECD1";
          ctx.textAlign = "left";
          ctx.fillText(
            winningText,
            labelX + labelPadding,
            labelY + labelHeight / 2 + 2
          );

          // Draw profit percentage
          ctx.font = profitFont;
          ctx.fillStyle = "#4ADE80";
          ctx.fillText(
            profitText,
            labelX + labelPadding + winningWidth + labelSpacing,
            labelY + labelHeight / 2 + 2
          );
        }
      }
    }

    if (dimension === ShareImageType.TWITTER) {
      // Dripit Logo Background
      const logoX = rightX + 486 + 16 - 32 - 24 + 6;
      const logoY = rightY - 10 - 2;
      const logoWidth = 100;
      const logoHeight = rightHeight;

      // Add shadow
      ctx.shadowColor = "rgba(0, 0, 0, 0.30)";
      ctx.shadowBlur = 25;
      ctx.shadowOffsetX = -10;
      ctx.shadowOffsetY = 10;

      // Create gradient
      ctx.fillStyle = "#0F172A"; // slate-900

      // Draw path
      ctx.beginPath();
      ctx.moveTo(logoX + 281, logoY + 122);
      ctx.lineTo(logoX + 54.8805, logoY + 122);
      ctx.bezierCurveTo(
        logoX + 42.9882,
        logoY + 122,
        logoX + 34.0189,
        logoY + 111.199,
        logoX + 36.2039,
        logoY + 99.5091
      );
      ctx.lineTo(logoX + 49.1011, logoY + 30.5091);
      ctx.bezierCurveTo(
        logoX + 50.7817,
        logoY + 21.5177,
        logoX + 58.6305,
        logoY + 15,
        logoX + 67.7776,
        logoY + 15
      );
      ctx.lineTo(logoX + 281, logoY + 15);
      ctx.closePath();
      ctx.fill();

      // Reset shadow
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Load the SVG logo
      const logoImage = await loadImage(FullLogoImg);

      ctx.drawImage(
        logoImage,
        logoX + 167.5 - 76.7, // Center the logo (118*1.3/2 = 76.7)
        logoY + 68.5 - 26, // Center the logo (40*1.3/2 = 26)
        153.4, // Original SVG width * 1.3
        52 // Original SVG height * 1.3
      );
      ctx.filter = "none"; // Reset filter
    }
    ctx.restore();

    // VS Circle
    ctx.beginPath();
    ctx.arc(680, 129, 105, 0, Math.PI * 2);
    ctx.fillStyle = "#0F172A";
    ctx.shadowColor = "rgba(0, 0, 0, 0.5)";
    ctx.shadowBlur = 20;
    ctx.shadowOffsetY = 10;
    ctx.fill();
    ctx.shadowColor = "transparent";

    // VS Text
    ctx.font = "bold 48px PhonkRegular";
    ctx.fillStyle = "white";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("VS", 680, 129);
  } else if (
    dimension === ShareImageType.INSTAGRAM_POST ||
    dimension === ShareImageType.INSTAGRAM_STORY
  ) {
    // Common elements for both Instagram post and story
    // Background
    ctx.fillStyle = "#0F172A";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Load artist images
    const [artist1Image, artist2Image] = await Promise.all([
      loadImage(battle.battleSides[0].artist.image),
      loadImage(battle.battleSides[1].artist.image),
    ]);

    if (dimension === ShareImageType.INSTAGRAM_POST) {
      // Instagram post specific layout
      // Draw artist images with post-specific paths
      ctx.save();
      // Left artist with SVG path
      ctx.beginPath();
      ctx.moveTo(393.13, 852 - 100 + 40);
      ctx.lineTo(447.914, 414.321);
      ctx.lineTo(565.608, 441.464);
      ctx.lineTo(593, -3);
      ctx.lineTo(-3, -3);
      ctx.lineTo(-3, 852 - 100 + 40);
      ctx.closePath();
      ctx.strokeStyle = "white";
      ctx.lineWidth = 5;
      ctx.stroke();
      ctx.clip();

      // Draw image to match path width
      const leftPathWidth = 593 - -3;
      const leftPathHeight = 852 - 100 + 40 - -3;
      ctx.drawImage(
        artist1Image,
        -3 - 50,
        -3,
        leftPathWidth + 100,
        leftPathHeight
      );

      // Add black and white effect with lighter opacity if this is the unselected artist
      if (unselectedSideId === battle.battleSides[0].id) {
        ctx.globalCompositeOperation = "color";
        ctx.fillStyle = "white";
        ctx.fillRect(-3 - 50, -3, leftPathWidth + 100, leftPathHeight);
        ctx.globalCompositeOperation = "source-over";
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = "black";
        ctx.fillRect(-3 - 50, -3, leftPathWidth + 100, leftPathHeight);
        ctx.globalAlpha = 0.2;
        ctx.fillStyle = "white";
        ctx.fillRect(-3 - 50, -3, leftPathWidth + 100, leftPathHeight);
        ctx.globalAlpha = 0.3; // Add slate-900 overlay
        ctx.fillStyle = "#0F172A"; // slate-900
        ctx.fillRect(-3 - 50, -3, leftPathWidth + 100, leftPathHeight);
        ctx.globalAlpha = 1;
      }

      // Add top gradient overlay
      const topGradient1 = ctx.createLinearGradient(0, 0, 0, leftPathHeight);
      topGradient1.addColorStop(0, "rgba(15, 23, 42, 0)");
      topGradient1.addColorStop(0.9, "#0F172A");
      ctx.fillStyle = topGradient1;
      ctx.fillRect(-3 - 50, -3, leftPathWidth + 100, leftPathHeight);
      ctx.restore();

      // Right artist with SVG path
      ctx.save();
      ctx.beginPath();
      // Move the path to the right side (1080 - 662 = 418px offset)
      const rightOffset = 418;
      ctx.moveTo(664 + rightOffset, 851.5 - 100 + 40);
      ctx.lineTo(664 + rightOffset, -3);
      ctx.lineTo(203.835 + rightOffset, -3);
      ctx.lineTo(176.448 + rightOffset, 480.583);
      ctx.lineTo(58.7738 + rightOffset, 454.266);
      ctx.lineTo(2 + rightOffset, 851.5 - 100 + 40);
      ctx.closePath();
      ctx.strokeStyle = "white";
      ctx.lineWidth = 5;
      ctx.stroke();
      ctx.clip();

      // Draw image to match path width
      const rightPathWidth = 662 - 2;
      const rightPathHeight = 851.5 - 100 + 40 - -3;
      ctx.drawImage(
        artist2Image,
        2 + rightOffset - 50,
        -3,
        rightPathWidth + 100,
        rightPathHeight
      );

      // Add black and white effect with lighter opacity if this is the unselected artist
      if (unselectedSideId === battle.battleSides[1].id) {
        ctx.globalCompositeOperation = "color";
        ctx.fillStyle = "white";
        ctx.fillRect(
          2 + rightOffset - 50,
          -3,
          rightPathWidth + 100,
          rightPathHeight
        );
        ctx.globalCompositeOperation = "source-over";
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = "black";
        ctx.fillRect(
          2 + rightOffset - 50,
          -3,
          rightPathWidth + 100,
          rightPathHeight
        );
        ctx.globalAlpha = 0.2;
        ctx.fillStyle = "white";
        ctx.fillRect(
          2 + rightOffset - 50,
          -3,
          rightPathWidth + 100,
          rightPathHeight
        );
        ctx.globalAlpha = 0.3; // Add slate-900 overlay
        ctx.fillStyle = "#0F172A"; // slate-900
        ctx.fillRect(
          2 + rightOffset - 50,
          -3,
          rightPathWidth + 100,
          rightPathHeight
        );
        ctx.globalAlpha = 1;
      }

      // Add top gradient overlay
      const topGradient2 = ctx.createLinearGradient(0, 0, 0, rightPathHeight);
      topGradient2.addColorStop(0, "rgba(15, 23, 42, 0)");
      topGradient2.addColorStop(0.9, "#0F172A");
      ctx.fillStyle = topGradient2;
      ctx.fillRect(
        2 + rightOffset - 50,
        -3,
        rightPathWidth + 100,
        rightPathHeight
      );
      ctx.restore();

      // Add full screen gradient overlay
      const fullScreenGradient = ctx.createLinearGradient(
        0,
        0,
        0,
        canvas.height
      );
      fullScreenGradient.addColorStop(0, "rgba(15, 23, 42, 0)");
      fullScreenGradient.addColorStop(0.58, "#0F172A");
      ctx.fillStyle = fullScreenGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Left artist name background and text
      const hasLeftPosition =
        showPosition &&
        battle.userPositions?.some(
          (pos) => pos.battle_side_id === battle.battleSides[0].id
        );
      const leftPositionSize =
        showPositionSize &&
        battle.userPositions?.find(
          (pos) => pos.battle_side_id === battle.battleSides[0].id
        )?.amount;
      ctx.fillStyle = hasLeftPosition ? "#FFFFFF" : "#1E293B"; // slate-800
      const leftX = 80; // Center the name box horizontally (1080/2 - 920/2)
      const leftY = canvas.height / 2 - 164 - 16 + 400; // Center of the page minus full height and half of gap, plus 400px down
      const leftWidth = 920;
      const leftHeight = 164;
      const radius = 27;

      // Check if left side won
      const leftSideWon =
        battle.battleSides[0].amount > battle.battleSides[1].amount;
      const isLeftWinner =
        hasLeftPosition && leftSideWon && battle.winner_id !== null;

      if (isLeftWinner) {
        // Create gradient for winner
        const gradient = ctx.createLinearGradient(
          leftX,
          leftY,
          leftX + leftWidth,
          leftY
        );
        gradient.addColorStop(0, "#45E2C0");
        gradient.addColorStop(1, "#C1FFE4");
        ctx.fillStyle = gradient;
      } else {
        ctx.fillStyle = hasLeftPosition ? "#FFFFFF" : "#1E293B";
      }

      // Draw rounded rectangle background
      ctx.beginPath();
      ctx.moveTo(leftX + radius, leftY);
      ctx.lineTo(leftX + leftWidth - radius, leftY);
      ctx.quadraticCurveTo(
        leftX + leftWidth,
        leftY,
        leftX + leftWidth,
        leftY + radius
      );
      ctx.lineTo(leftX + leftWidth, leftY + leftHeight - radius);
      ctx.quadraticCurveTo(
        leftX + leftWidth,
        leftY + leftHeight,
        leftX + leftWidth - radius,
        leftY + leftHeight
      );
      ctx.lineTo(leftX + radius, leftY + leftHeight);
      ctx.quadraticCurveTo(
        leftX,
        leftY + leftHeight,
        leftX,
        leftY + leftHeight - radius
      );
      ctx.lineTo(leftX, leftY + radius);
      ctx.quadraticCurveTo(leftX, leftY, leftX + radius, leftY);
      ctx.closePath();
      ctx.fill();

      // Left artist name
      ctx.font = "44px PhonkRegular";
      ctx.fillStyle = hasLeftPosition ? "#0F172A" : "white";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(
        battle.battleSides[0].artist.name,
        leftX + 32,
        leftY + (showPositionSize && leftPositionSize ? 56 : leftHeight / 2)
      );

      // Left position size
      if (showPositionSize && leftPositionSize) {
        ctx.font = "800 32px GeistVF";
        ctx.fillStyle = hasLeftPosition ? "#0F172A" : "white";
        const text = `$ ${usdcToUsd(Number(leftPositionSize)).toFixed(2)}`;
        const textWidth = ctx.measureText(text).width;

        // Draw text
        ctx.fillText(text, leftX + 32, leftY + leftHeight - 52);

        // Draw trophy if artist won and battle is resolved
        if (isLeftWinner) {
          const trophyImage = await loadImage(TrophyCircle);
          const trophySize = 64;
          ctx.drawImage(
            trophyImage,
            leftX + 360 + textWidth + 48,
            leftY + leftHeight - 52 - trophySize / 2,
            trophySize,
            trophySize
          );
        }
      }

      // Right artist name background and text
      const hasRightPosition =
        showPosition &&
        battle.userPositions?.some(
          (pos) => pos.battle_side_id === battle.battleSides[1].id
        );
      const rightPositionSize =
        showPositionSize &&
        battle.userPositions?.find(
          (pos) => pos.battle_side_id === battle.battleSides[1].id
        )?.amount;

      // Check if right side won
      const rightSideWon =
        battle.battleSides[1].amount > battle.battleSides[0].amount;
      const isRightWinner =
        hasRightPosition && rightSideWon && battle.winner_id !== null;

      const rightX = 80;
      const rightY = canvas.height / 2 + 16 + 400;
      const rightWidth = 920;
      const rightHeight = 164;

      if (isRightWinner) {
        // Create gradient for winner
        const gradient = ctx.createLinearGradient(
          rightX,
          rightY,
          rightX + rightWidth,
          rightY
        );
        gradient.addColorStop(0, "#45E2C0");
        gradient.addColorStop(1, "#C1FFE4");
        ctx.fillStyle = gradient;
      } else {
        ctx.fillStyle = hasRightPosition ? "#FFFFFF" : "#1E293B";
      }

      // Draw rounded rectangle background
      ctx.beginPath();
      ctx.moveTo(rightX + radius, rightY);
      ctx.lineTo(rightX + rightWidth - radius, rightY);
      ctx.quadraticCurveTo(
        rightX + rightWidth,
        rightY,
        rightX + rightWidth,
        rightY + radius
      );
      ctx.lineTo(rightX + rightWidth, rightY + rightHeight - radius);
      ctx.quadraticCurveTo(
        rightX + rightWidth,
        rightY + rightHeight,
        rightX + rightWidth - radius,
        rightY + rightHeight
      );
      ctx.lineTo(rightX + radius, rightY + rightHeight);
      ctx.quadraticCurveTo(
        rightX,
        rightY + rightHeight,
        rightX,
        rightY + rightHeight - radius
      );
      ctx.lineTo(rightX, rightY + radius);
      ctx.quadraticCurveTo(rightX, rightY, rightX + radius, rightY);
      ctx.closePath();
      ctx.fill();

      // Right artist name
      ctx.font = "44px PhonkRegular";
      ctx.fillStyle = hasRightPosition ? "#0F172A" : "white";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(
        battle.battleSides[1].artist.name,
        rightX + 32,
        rightY + (showPositionSize && rightPositionSize ? 56 : rightHeight / 2)
      );

      // Right position size
      if (showPositionSize && rightPositionSize) {
        ctx.font = "800 32px GeistVF";
        ctx.fillStyle = hasRightPosition ? "#0F172A" : "white";
        const text = `$ ${usdcToUsd(Number(rightPositionSize)).toFixed(2)}`;
        const textWidth = ctx.measureText(text).width;

        // Draw text
        ctx.fillText(text, rightX + 32, rightY + rightHeight - 52);

        // Draw trophy if artist won and battle is resolved
        if (isRightWinner) {
          const trophyImage = await loadImage(TrophyCircle);
          const trophySize = 64;
          ctx.drawImage(
            trophyImage,
            rightX + 360 + textWidth + 48,
            rightY + rightHeight - 52 - trophySize / 2,
            trophySize,
            trophySize
          );
        }
      }

      // VS Circle Background
      ctx.beginPath();
      ctx.arc(
        540 + 200 + 50 + 24,
        leftY + leftHeight + 16,
        140,
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "#0F172A"; // slate-900
      ctx.fill();

      // Add SVG shape above VS circle
      const svgX = 540 + 200 + 50 + 24 - 140;
      const svgY = leftY + leftHeight + 16 - 183 - 20 - 100 - 24 + 12;

      // Create gradieneft t for SVG
      const svgGradient = ctx.createLinearGradient(
        svgX + 222,
        svgY + 168,
        svgX + 92.5,
        svgY - 32
      );
      svgGradient.addColorStop(0, "#C1FFE4");
      svgGradient.addColorStop(1, "#45E2C0");

      // Draw SVG path
      ctx.beginPath();
      ctx.moveTo(svgX + 265, svgY + 72.8145);
      ctx.bezierCurveTo(
        svgX + 265,
        svgY + 83.9915,
        svgX + 258.786,
        svgY + 94.2419,
        svgX + 248.877,
        svgY + 99.412
      );
      ctx.lineTo(svgX + 163.877, svgY + 143.76);
      ctx.bezierCurveTo(
        svgX + 155.182,
        svgY + 148.296,
        svgX + 144.818,
        svgY + 148.296,
        svgX + 136.123,
        svgY + 143.76
      );
      ctx.lineTo(svgX + 51.123, svgY + 99.412);
      ctx.bezierCurveTo(
        svgX + 41.2136,
        svgY + 94.2419,
        svgX + 35,
        svgY + 83.9915,
        svgX + 35,
        svgY + 72.8145
      );
      ctx.lineTo(svgX + 35, svgY + 45);
      ctx.bezierCurveTo(
        svgX + 35,
        svgY + 28.4315,
        svgX + 48.4315,
        svgY + 15,
        svgX + 65,
        svgY + 15
      );
      ctx.lineTo(svgX + 235, svgY + 15);
      ctx.bezierCurveTo(
        svgX + 251.569,
        svgY + 15,
        svgX + 265,
        svgY + 28.4315,
        svgX + 265,
        svgY + 45
      );
      ctx.closePath();

      // Add shadow
      ctx.shadowColor = "rgba(0, 0, 0, 0.3)";
      ctx.shadowBlur = 25;
      ctx.shadowOffsetX = -10;
      ctx.shadowOffsetY = 10;
      ctx.fillStyle = svgGradient;
      ctx.fill();

      // Reset shadow
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Load the SVG logo
      const logoImage = await loadSvgFile(fullLogoSvg, "#0F172A");

      ctx.drawImage(
        logoImage,
        svgX + 140 + 8 - 76.7, // Center the logo (118*1.3/2 = 76.7)
        svgY + 81.5 - 12 - 26, // Center the logo (40*1.3/2 = 26)
        153.4, // Original SVG width * 1.3
        52 // Original SVG height * 1.3
      );
      ctx.filter = "none"; // Reset filter

      // VS Circle
      ctx.beginPath();
      ctx.arc(
        540 + 200 + 50 + 24,
        leftY + leftHeight + 16,
        105,
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "#475569"; // slate-600
      ctx.shadowColor = "rgba(0, 0, 0, 0.50)";
      ctx.shadowBlur = 25;
      ctx.shadowOffsetX = -12.575;
      ctx.shadowOffsetY = 12.575;
      ctx.fill();
      ctx.shadowColor = "transparent";

      // VS Text
      ctx.font = "60px PhonkRegular";
      ctx.fillStyle = "white";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("VS", 540 + 200 + 50 + 24, leftY + leftHeight + 16);

      // --- DRAW WINNINGS LABELS LAST SO THEY ARE ON TOP ---
      // Left winner label
      if (showWinnings && isLeftWinner) {
        const claim = battle.claims?.find(
          (claim) => claim.position_id === battle.userPositions?.[0]?.id
        );
        if (claim) {
          const winningAmount = usdcToUsd(Number(claim.amount));
          const positionSize = battle.userPositions?.find(
            (pos) => pos.battle_side_id === battle.battleSides[0].id
          )?.amount;
          const profitPercentage = positionSize
            ? (winningAmount / usdcToUsd(Number(positionSize)) - 1) * 100
            : 0;

          const labelPadding = 16;
          const labelSpacing = 8;
          const labelY = leftY - 72;
          const labelHeight = 56;
          const labelRadius = 12;

          ctx.font = "800 32px PhonkRegular";
          const winningText = `$ ${winningAmount.toFixed(2)}`;
          const winningWidth = ctx.measureText(winningText).width;

          ctx.font = "600 24px GeistVF";
          const profitText = `+${profitPercentage.toFixed(1)}%`;
          const profitWidth = ctx.measureText(profitText).width;

          const totalWidth =
            winningWidth + profitWidth + labelPadding * 2 + labelSpacing;
          const labelX = leftSideWon
            ? leftX + 4
            : leftX + 16 + totalWidth - totalWidth + 12;

          ctx.beginPath();
          ctx.moveTo(labelX + labelRadius, labelY);
          ctx.lineTo(labelX + totalWidth - labelRadius, labelY);
          ctx.quadraticCurveTo(
            labelX + totalWidth,
            labelY,
            labelX + totalWidth,
            labelY + labelRadius
          );
          ctx.lineTo(labelX + totalWidth, labelY + labelHeight - labelRadius);
          ctx.quadraticCurveTo(
            labelX + totalWidth,
            labelY + labelHeight,
            labelX + totalWidth - labelRadius,
            labelY + labelHeight
          );
          ctx.lineTo(labelX + labelRadius, labelY + labelHeight);
          ctx.quadraticCurveTo(
            labelX,
            labelY + labelHeight,
            labelX,
            labelY + labelHeight - labelRadius
          );
          ctx.lineTo(labelX, labelY + labelRadius);
          ctx.quadraticCurveTo(labelX, labelY, labelX + labelRadius, labelY);
          ctx.closePath();

          ctx.fillStyle = "#0F172A";
          ctx.fill();
          ctx.strokeStyle = "#A2ECD1";
          ctx.lineWidth = 2;
          ctx.stroke();

          ctx.font = "800 32px PhonkRegular";
          ctx.fillStyle = "#A2ECD1";
          ctx.textAlign = "left";
          ctx.fillText(
            winningText,
            labelX + labelPadding,
            labelY + labelHeight / 2 + 2
          );

          ctx.font = "600 24px GeistVF";
          ctx.fillStyle = "#4ADE80";
          ctx.fillText(
            profitText,
            labelX + labelPadding + winningWidth + labelSpacing,
            labelY + labelHeight / 2 + 2
          );
        }
      }
      // Right winner label
      if (showWinnings && isRightWinner) {
        const claim = battle.claims?.find(
          (claim) => claim.position_id === battle.userPositions?.[0]?.id
        );
        if (claim) {
          const winningAmount = usdcToUsd(Number(claim.amount));
          const positionSize = battle.userPositions?.find(
            (pos) => pos.battle_side_id === battle.battleSides[1].id
          )?.amount;
          const profitPercentage = positionSize
            ? (winningAmount / usdcToUsd(Number(positionSize)) - 1) * 100
            : 0;

          const labelPadding = 16;
          const labelSpacing = 8;
          const labelY = rightY - 280; // Changed from -72 to -172 to move 100px up
          const labelHeight = 56;
          const labelRadius = 12;

          ctx.font = "800 32px PhonkRegular";
          const winningText = `$ ${winningAmount.toFixed(2)}`;
          const winningWidth = ctx.measureText(winningText).width;

          ctx.font = "600 24px GeistVF";
          const profitText = `+${profitPercentage.toFixed(1)}%`;
          const profitWidth = ctx.measureText(profitText).width;

          const totalWidth =
            winningWidth + profitWidth + labelPadding * 2 + labelSpacing;
          const labelX = rightSideWon
            ? rightX +
              4 +
              ((dimension as ShareImageType) === ShareImageType.TWITTER
                ? 100
                : 0) // Add 100px only for Twitter
            : rightX + 16 + totalWidth - totalWidth + 12;

          ctx.beginPath();
          ctx.moveTo(labelX + labelRadius, labelY);
          ctx.lineTo(labelX + totalWidth - labelRadius, labelY);
          ctx.quadraticCurveTo(
            labelX + totalWidth,
            labelY,
            labelX + totalWidth,
            labelY + labelRadius
          );
          ctx.lineTo(labelX + totalWidth, labelY + labelHeight - labelRadius);
          ctx.quadraticCurveTo(
            labelX + totalWidth,
            labelY + labelHeight,
            labelX + totalWidth - labelRadius,
            labelY + labelHeight
          );
          ctx.lineTo(labelX + labelRadius, labelY + labelHeight);
          ctx.quadraticCurveTo(
            labelX,
            labelY + labelHeight,
            labelX,
            labelY + labelHeight - labelRadius
          );
          ctx.lineTo(labelX, labelY + labelRadius);
          ctx.quadraticCurveTo(labelX, labelY, labelX + labelRadius, labelY);
          ctx.closePath();

          ctx.fillStyle = "#0F172A";
          ctx.fill();
          ctx.strokeStyle = "#A2ECD1";
          ctx.lineWidth = 2;
          ctx.stroke();

          ctx.font = "800 32px PhonkRegular";
          ctx.fillStyle = "#A2ECD1";
          ctx.textAlign = "left";
          ctx.fillText(
            winningText,
            labelX + labelPadding,
            labelY + labelHeight / 2 + 2
          );

          ctx.font = "600 24px GeistVF";
          ctx.fillStyle = "#4ADE80";
          ctx.fillText(
            profitText,
            labelX + labelPadding + winningWidth + labelSpacing,
            labelY + labelHeight / 2 + 2
          );
        }
      }
    } else if (dimension === ShareImageType.INSTAGRAM_STORY) {
      // Instagram story specific layout
      // Left artist with SVG path
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(393.13, 1267);
      ctx.lineTo(447.914, 636.881);
      ctx.lineTo(565.608, 677.198);
      ctx.lineTo(593, -3);
      ctx.lineTo(-3, -3);
      ctx.lineTo(-3, 1267);
      ctx.closePath();
      ctx.strokeStyle = "white";
      ctx.lineWidth = 5;
      ctx.stroke();
      ctx.clip();

      // Draw image to match path width
      const leftPathWidth = 593 - -3;
      const leftPathHeight = 1267 - -3;
      ctx.drawImage(
        artist1Image,
        -3 - 50 - 150,
        -3,
        leftPathWidth + 100 + 300,
        leftPathHeight
      );

      // Add black and white effect with lighter opacity if this is the unselected artist
      if (unselectedSideId === battle.battleSides[0].id) {
        ctx.globalCompositeOperation = "color";
        ctx.fillStyle = "white";
        ctx.fillRect(
          -3 - 50 - 150,
          -3,
          leftPathWidth + 100 + 300,
          leftPathHeight
        );
        ctx.globalCompositeOperation = "source-over";
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = "black";
        ctx.fillRect(
          -3 - 50 - 150,
          -3,
          leftPathWidth + 100 + 300,
          leftPathHeight
        );
        ctx.globalAlpha = 0.2;
        ctx.fillStyle = "white";
        ctx.fillRect(
          -3 - 50 - 150,
          -3,
          leftPathWidth + 100 + 300,
          leftPathHeight
        );
        ctx.globalAlpha = 0.3; // Add slate-900 overlay
        ctx.fillStyle = "#0F172A"; // slate-900
        ctx.fillRect(
          -3 - 50 - 150,
          -3,
          leftPathWidth + 100 + 300,
          leftPathHeight
        );
        ctx.globalAlpha = 1;
      }

      // Add top gradient overlay
      const topGradient1 = ctx.createLinearGradient(0, 0, 0, leftPathHeight);
      topGradient1.addColorStop(0, "rgba(15, 23, 42, 0)");
      topGradient1.addColorStop(0.9, "#0F172A");
      ctx.fillStyle = topGradient1;
      ctx.fillRect(
        -3 - 50 - 150,
        -3,
        leftPathWidth + 100 + 300,
        leftPathHeight
      );
      ctx.restore();

      // Right artist with SVG path
      ctx.save();
      ctx.beginPath();
      // Move the path to the right side (1080 - 662 = 418px offset)
      const rightOffset = 418;
      ctx.moveTo(664 + rightOffset, 1267);
      ctx.lineTo(664 + rightOffset, -3);
      ctx.lineTo(203.835 + rightOffset, -3);
      ctx.lineTo(176.448 + rightOffset, 717.833);
      ctx.lineTo(56.7738 + rightOffset, 677.516);
      ctx.lineTo(2 + rightOffset, 1267);
      ctx.closePath();
      ctx.strokeStyle = "white";
      ctx.lineWidth = 5;
      ctx.stroke();
      ctx.clip();

      // Draw image to match path width
      const rightPathWidth = 662 - 2;
      const rightPathHeight = 1267 - -3;
      ctx.drawImage(
        artist2Image,
        2 + rightOffset - 50 - 150,
        -3,
        rightPathWidth + 100 + 300,
        rightPathHeight
      );

      // Add black and white effect with lighter opacity if this is the unselected artist
      if (unselectedSideId === battle.battleSides[1].id) {
        ctx.globalCompositeOperation = "color";
        ctx.fillStyle = "white";
        ctx.fillRect(
          2 + rightOffset - 50 - 150,
          -3,
          rightPathWidth + 100 + 300,
          rightPathHeight
        );
        ctx.globalCompositeOperation = "source-over";
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = "black";
        ctx.fillRect(
          2 + rightOffset - 50 - 150,
          -3,
          rightPathWidth + 100 + 300,
          rightPathHeight
        );
        ctx.globalAlpha = 0.2;
        ctx.fillStyle = "white";
        ctx.fillRect(
          2 + rightOffset - 50 - 150,
          -3,
          rightPathWidth + 100 + 300,
          rightPathHeight
        );
        ctx.globalAlpha = 0.3; // Add slate-900 overlay
        ctx.fillStyle = "#0F172A"; // slate-900
        ctx.fillRect(
          2 + rightOffset - 50 - 150,
          -3,
          rightPathWidth + 100 + 300,
          rightPathHeight
        );
        ctx.globalAlpha = 1;
      }

      // Add top gradient overlay
      const topGradient2 = ctx.createLinearGradient(0, 0, 0, rightPathHeight);
      topGradient2.addColorStop(0, "rgba(15, 23, 42, 0)");
      topGradient2.addColorStop(0.9, "#0F172A");
      ctx.fillStyle = topGradient2;
      ctx.fillRect(
        2 + rightOffset - 50 - 150,
        -3,
        rightPathWidth + 100 + 300,
        rightPathHeight
      );
      ctx.restore();

      // Add full screen gradient overlay
      const fullScreenGradient = ctx.createLinearGradient(
        0,
        0,
        0,
        canvas.height
      );
      fullScreenGradient.addColorStop(0, "rgba(15, 23, 42, 0)");
      fullScreenGradient.addColorStop(0.58, "#0F172A");
      ctx.fillStyle = fullScreenGradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Left artist name background and text
      const hasLeftPosition =
        showPosition &&
        battle.userPositions?.some(
          (pos) => pos.battle_side_id === battle.battleSides[0].id
        );
      const leftPositionSize =
        showPositionSize &&
        battle.userPositions?.find(
          (pos) => pos.battle_side_id === battle.battleSides[0].id
        )?.amount;
      ctx.fillStyle = hasLeftPosition ? "#FFFFFF" : "#1E293B"; // slate-800
      const leftX = 80; // Center the name box horizontally (1080/2 - 920/2)
      const leftY =
        dimension === ShareImageType.INSTAGRAM_STORY
          ? canvas.height / 2 - 164 - 16 + 400 // Moved down by another 100px for Story
          : canvas.height / 2 - 164 - 16 + 400 + 200;
      const leftWidth = 920;
      const leftHeight = 164;
      const radius = 27;

      // Check if left side won
      const leftSideWon =
        battle.battleSides[0].amount > battle.battleSides[1].amount;
      const isLeftWinner =
        hasLeftPosition && leftSideWon && battle.winner_id !== null;

      if (isLeftWinner) {
        // Create gradient for winner
        const gradient = ctx.createLinearGradient(
          leftX,
          leftY,
          leftX + leftWidth,
          leftY
        );
        gradient.addColorStop(0, "#45E2C0");
        gradient.addColorStop(1, "#C1FFE4");
        ctx.fillStyle = gradient;
      } else {
        ctx.fillStyle = hasLeftPosition ? "#FFFFFF" : "#1E293B";
      }

      // Draw rounded rectangle background
      ctx.beginPath();
      ctx.moveTo(leftX + radius, leftY);
      ctx.lineTo(leftX + leftWidth - radius, leftY);
      ctx.quadraticCurveTo(
        leftX + leftWidth,
        leftY,
        leftX + leftWidth,
        leftY + radius
      );
      ctx.lineTo(leftX + leftWidth, leftY + leftHeight - radius);
      ctx.quadraticCurveTo(
        leftX + leftWidth,
        leftY + leftHeight,
        leftX + leftWidth - radius,
        leftY + leftHeight
      );
      ctx.lineTo(leftX + radius, leftY + leftHeight);
      ctx.quadraticCurveTo(
        leftX,
        leftY + leftHeight,
        leftX,
        leftY + leftHeight - radius
      );
      ctx.lineTo(leftX, leftY + radius);
      ctx.quadraticCurveTo(leftX, leftY, leftX + radius, leftY);
      ctx.closePath();
      ctx.fill();

      // Left artist name
      ctx.font = "44px PhonkRegular";
      ctx.fillStyle = hasLeftPosition ? "#0F172A" : "white";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(
        battle.battleSides[0].artist.name,
        leftX + 32,
        leftY + (showPositionSize && leftPositionSize ? 43 : leftHeight / 2) // Moved 8px lower (from 35 to 43)
      );

      // Left position size
      if (showPositionSize && leftPositionSize) {
        ctx.font = "800 32px GeistVF";
        ctx.fillStyle = hasLeftPosition ? "#0F172A" : "white";
        const text = `$ ${usdcToUsd(Number(leftPositionSize)).toFixed(2)}`;
        const textWidth = ctx.measureText(text).width;

        // Draw text
        ctx.fillText(text, leftX + 32, leftY + leftHeight - 52); // Moved 12px higher (from -40 to -52)

        // Draw trophy if artist won and battle is resolved
        if (isLeftWinner) {
          const trophyImage = await loadImage(TrophyCircle);
          const trophySize = 64;
          ctx.drawImage(
            trophyImage,
            leftX + 360 + textWidth + 48,
            leftY + leftHeight - 52 - trophySize / 2,
            trophySize,
            trophySize
          );
        }
      }

      // Draw winning amount label for Instagram story and post
      if (showWinnings && isLeftWinner) {
        const claim = battle.claims?.find(
          (claim) => claim.position_id === battle.userPositions?.[0]?.id
        );
        if (claim) {
          const winningAmount = usdcToUsd(Number(claim.amount));
          const positionSize = battle.userPositions?.find(
            (pos) => pos.battle_side_id === battle.battleSides[0].id
          )?.amount;
          const profitPercentage = positionSize
            ? (winningAmount / usdcToUsd(Number(positionSize)) - 1) * 100
            : 0;

          // Draw label background
          const labelPadding = 16;
          const labelSpacing = 8;
          const labelY = leftY - 72;
          const labelHeight = 56;
          const labelRadius = 12;

          // Calculate text widths
          ctx.font = "800 32px PhonkRegular";
          const winningText = `$ ${winningAmount.toFixed(2)}`;
          const winningWidth = ctx.measureText(winningText).width;

          ctx.font = "600 24px GeistVF";
          const profitText = `+${profitPercentage.toFixed(1)}%`;
          const profitWidth = ctx.measureText(profitText).width;

          const totalWidth =
            winningWidth + profitWidth + labelPadding * 2 + labelSpacing;

          // Determine label alignment based on winner
          const labelX = leftSideWon
            ? leftX + 4
            : leftX + 16 + totalWidth - totalWidth + 12;

          // Draw rounded rectangle background
          ctx.beginPath();
          ctx.moveTo(labelX + labelRadius, labelY);
          ctx.lineTo(labelX + totalWidth - labelRadius, labelY);
          ctx.quadraticCurveTo(
            labelX + totalWidth,
            labelY,
            labelX + totalWidth,
            labelY + labelRadius
          );
          ctx.lineTo(labelX + totalWidth, labelY + labelHeight - labelRadius);
          ctx.quadraticCurveTo(
            labelX + totalWidth,
            labelY + labelHeight,
            labelX + totalWidth - labelRadius,
            labelY + labelHeight
          );
          ctx.lineTo(labelX + labelRadius, labelY + labelHeight);
          ctx.quadraticCurveTo(
            labelX,
            labelY + labelHeight,
            labelX,
            labelY + labelHeight - labelRadius
          );
          ctx.lineTo(labelX, labelY + labelRadius);
          ctx.quadraticCurveTo(labelX, labelY, labelX + labelRadius, labelY);
          ctx.closePath();

          // Fill background
          ctx.fillStyle = "#0F172A";
          ctx.fill();

          // Draw border
          ctx.strokeStyle = "#A2ECD1";
          ctx.lineWidth = 2;
          ctx.stroke();

          // Draw winning amount
          ctx.font = "800 32px PhonkRegular";
          ctx.fillStyle = "#A2ECD1";
          ctx.textAlign = "left";
          ctx.fillText(
            winningText,
            labelX + labelPadding,
            labelY + labelHeight / 2 + 2
          );

          // Draw profit percentage
          ctx.font = "600 24px GeistVF";
          ctx.fillStyle = "#4ADE80";
          ctx.fillText(
            profitText,
            labelX + labelPadding + winningWidth + labelSpacing,
            labelY + labelHeight / 2 + 2
          );
        }
      }

      // Right artist name background and text
      const hasRightPosition =
        showPosition &&
        battle.userPositions?.some(
          (pos) => pos.battle_side_id === battle.battleSides[1].id
        );
      const rightPositionSize =
        showPositionSize &&
        battle.userPositions?.find(
          (pos) => pos.battle_side_id === battle.battleSides[1].id
        )?.amount;
      ctx.fillStyle = hasRightPosition ? "#FFFFFF" : "#1E293B"; // slate-800
      const rightX = 80; // Same x position as left artist
      const rightY =
        dimension === ShareImageType.INSTAGRAM_STORY
          ? canvas.height / 2 + 16 + 400 // Moved down by another 100px for Story
          : canvas.height / 2 + 16 + 400 + 200;
      const rightWidth = 920;
      const rightHeight = 164;

      // Check if right side won
      const rightSideWon =
        battle.battleSides[1].amount > battle.battleSides[0].amount;
      const isRightWinner =
        hasRightPosition && rightSideWon && battle.winner_id !== null;

      if (isRightWinner) {
        // Create gradient for winner
        const gradient = ctx.createLinearGradient(
          rightX,
          rightY,
          rightX + rightWidth,
          rightY
        );
        gradient.addColorStop(0, "#45E2C0");
        gradient.addColorStop(1, "#C1FFE4");
        ctx.fillStyle = gradient;
      } else {
        ctx.fillStyle = hasRightPosition ? "#FFFFFF" : "#1E293B";
      }

      // Draw rounded rectangle background
      ctx.beginPath();
      ctx.moveTo(rightX + radius, rightY);
      ctx.lineTo(rightX + rightWidth - radius, rightY);
      ctx.quadraticCurveTo(
        rightX + rightWidth,
        rightY,
        rightX + rightWidth,
        rightY + radius
      );
      ctx.lineTo(rightX + rightWidth, rightY + rightHeight - radius);
      ctx.quadraticCurveTo(
        rightX + rightWidth,
        rightY + rightHeight,
        rightX + rightWidth - radius,
        rightY + rightHeight
      );
      ctx.lineTo(rightX + radius, rightY + rightHeight);
      ctx.quadraticCurveTo(
        rightX,
        rightY + rightHeight,
        rightX,
        rightY + rightHeight - radius
      );
      ctx.lineTo(rightX, rightY + radius);
      ctx.quadraticCurveTo(rightX, rightY, rightX + radius, rightY);
      ctx.closePath();
      ctx.fill();

      // Right artist name
      ctx.font = "44px PhonkRegular";
      ctx.fillStyle = hasRightPosition ? "#0F172A" : "white";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(
        battle.battleSides[1].artist.name,
        rightX + 32,
        rightY + (showPositionSize && rightPositionSize ? 43 : rightHeight / 2) // Moved 8px lower (from 35 to 43)
      );

      // Right position size
      if (showPositionSize && rightPositionSize) {
        ctx.font = "800 32px GeistVF";
        ctx.fillStyle = hasRightPosition ? "#0F172A" : "white";
        const text = `$ ${usdcToUsd(Number(rightPositionSize)).toFixed(2)}`;
        const textWidth = ctx.measureText(text).width;

        // Draw text
        ctx.fillText(text, rightX + 32, rightY + rightHeight - 52); // Moved 12px higher (from -40 to -52)

        // Draw trophy if artist won and battle is resolved
        if (isRightWinner) {
          const trophyImage = await loadImage(TrophyCircle);
          const trophySize = 64;
          ctx.drawImage(
            trophyImage,
            rightX + 350 + textWidth + 48, // Moved 40px more right (from +8 to +48)
            rightY + rightHeight - 20 - trophySize / 2 - 36,
            trophySize,
            trophySize
          );
        }
      }

      // VS Circle Background
      ctx.beginPath();
      ctx.arc(
        540 + 200 + 50 + 24,
        leftY + leftHeight + 16,
        140,
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "#0F172A"; // slate-900
      ctx.fill();

      // Add SVG shape above VS circle
      const svgX = 540 + 200 + 50 + 24 - 140;
      const svgY = leftY + leftHeight + 16 - 183 - 20 - 100 - 24 + 12;

      // Create gradient for SVG
      const svgGradient = ctx.createLinearGradient(
        svgX + 222,
        svgY + 168,
        svgX + 92.5,
        svgY - 32
      );
      svgGradient.addColorStop(0, "#C1FFE4");
      svgGradient.addColorStop(1, "#45E2C0");

      // Draw SVG path
      ctx.beginPath();
      ctx.moveTo(svgX + 265, svgY + 72.8145);
      ctx.bezierCurveTo(
        svgX + 265,
        svgY + 83.9915,
        svgX + 258.786,
        svgY + 94.2419,
        svgX + 248.877,
        svgY + 99.412
      );
      ctx.lineTo(svgX + 163.877, svgY + 143.76);
      ctx.bezierCurveTo(
        svgX + 155.182,
        svgY + 148.296,
        svgX + 144.818,
        svgY + 148.296,
        svgX + 136.123,
        svgY + 143.76
      );
      ctx.lineTo(svgX + 51.123, svgY + 99.412);
      ctx.bezierCurveTo(
        svgX + 41.2136,
        svgY + 94.2419,
        svgX + 35,
        svgY + 83.9915,
        svgX + 35,
        svgY + 72.8145
      );
      ctx.lineTo(svgX + 35, svgY + 45);
      ctx.bezierCurveTo(
        svgX + 35,
        svgY + 28.4315,
        svgX + 48.4315,
        svgY + 15,
        svgX + 65,
        svgY + 15
      );
      ctx.lineTo(svgX + 235, svgY + 15);
      ctx.bezierCurveTo(
        svgX + 251.569,
        svgY + 15,
        svgX + 265,
        svgY + 28.4315,
        svgX + 265,
        svgY + 45
      );
      ctx.closePath();

      // Add shadow
      ctx.shadowColor = "rgba(0, 0, 0, 0.3)";
      ctx.shadowBlur = 25;
      ctx.shadowOffsetX = -10;
      ctx.shadowOffsetY = 10;
      ctx.fillStyle = svgGradient;
      ctx.fill();

      // Reset shadow
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Load the SVG logo
      const logoImage = await loadSvgFile(fullLogoSvg, "#0F172A");

      ctx.drawImage(
        logoImage,
        svgX + 140 + 8 - 76.7, // Center the logo (118*1.3/2 = 76.7)
        svgY + 81.5 - 12 - 26, // Center the logo (40*1.3/2 = 26)
        153.4, // Original SVG width * 1.3
        52 // Original SVG height * 1.3
      );
      ctx.filter = "none"; // Reset filter

      // VS Circle
      ctx.beginPath();
      ctx.arc(
        540 + 200 + 50 + 24,
        leftY + leftHeight + 16,
        105,
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "#475569"; // slate-600
      ctx.shadowColor = "rgba(0, 0, 0, 0.50)";
      ctx.shadowBlur = 25;
      ctx.shadowOffsetX = -12.575;
      ctx.shadowOffsetY = 12.575;
      ctx.fill();
      ctx.shadowColor = "transparent";

      // VS Text
      ctx.font = "60px PhonkRegular";
      ctx.fillStyle = "white";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("VS", 540 + 200 + 50 + 24, leftY + leftHeight + 16);
    }
  }

  // Add week number at top right (moved to end to appear above everything)
  const startDate = new Date(battle.start_date * 1000);

  // Get the first day of the year
  const firstDayOfYear = new Date(startDate.getFullYear(), 0, 1);

  // Calculate the week number
  const pastDaysOfYear =
    (startDate.getTime() - firstDayOfYear.getTime()) / 86400000;
  const weekNumber = Math.ceil(
    (pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7
  );

  // Only show week number at top for non-Instagram Story
  if (dimension !== ShareImageType.INSTAGRAM_STORY) {
    ctx.font = "900 32px GeistVF";
    ctx.fillStyle = "white";
    ctx.textAlign = "right";
    ctx.fillText(`WEEK ${weekNumber}`, canvas.width - 40, 60);
  }

  // Add QR code based on dimension
  if (dimension === ShareImageType.TWITTER && showQR) {
    await drawQRCodeWithText(ctx, 40, 40, 100);
  } else if (dimension === ShareImageType.INSTAGRAM_POST && showQR) {
    // For Head2Head Instagram Post, place QR at top left
    await drawQRCodeWithText(ctx, 60, 60, 140);
  } else if (dimension === ShareImageType.INSTAGRAM_STORY) {
    if (battle.type === BattlesType.head2head_battle) {
      // Add week number at bottom right (always show for Head2Head Story)
      ctx.font = "900 40px GeistVF";
      ctx.fillStyle = "white";
      ctx.textAlign = "right";
      ctx.fillText(
        `WEEK ${weekNumber}`,
        canvas.width - 80,
        canvas.height - 100
      );

      // Add QR code if enabled
      if (showQR) {
        await drawQRCodeWithText(ctx, 80, canvas.height - 280, 140);
      }
    }
  }

  // Convert to base64
  const base64Image = canvas.toDataURL("image/png");
  return base64Image;
};

export const generateHypeboardShareImage = async ({
  battle,
  dimension,
  shareInfo,
}: GenerateShareImageProps) => {
  if (!battle || !battle.hypeboard) return;

  const showQR = shareInfo.includes(ShareInfoType.ReferralCode);
  const showPosition = shareInfo.includes(ShareInfoType.Position);
  const showPositionSize = shareInfo.includes(ShareInfoType.PositionSize);
  const showWinnings = shareInfo.includes(ShareInfoType.Winnings);
  const canvas = document.createElement("canvas");

  // Set dimensions based on ShareImageType
  if (dimension === ShareImageType.TWITTER) {
    canvas.width = 1200;
    canvas.height = 675;
  } else if (dimension === ShareImageType.INSTAGRAM_POST) {
    canvas.width = 1080;
    canvas.height = 1350;
  } else if (dimension === ShareImageType.INSTAGRAM_STORY) {
    canvas.width = 1080;
    canvas.height = 1920;
  } else {
    return; // Return if unsupported dimension type
  }

  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  // Background
  ctx.fillStyle = "#0F172A";
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Load all artist images
  const validSides = battle.battleSides.filter((side) => side.artist?.image);
  if (validSides.length === 0) return;

  const artistImages = await Promise.all(
    validSides.map((side) => loadImage(side.artist!.image!))
  );

  // Grid configuration
  const GRID_ROWS =
    dimension === ShareImageType.INSTAGRAM_POST
      ? 8
      : dimension === ShareImageType.INSTAGRAM_STORY
        ? 9
        : 5;
  const GRID_COLS =
    dimension === ShareImageType.INSTAGRAM_POST
      ? 10
      : dimension === ShareImageType.INSTAGRAM_STORY
        ? 10
        : 12;
  const TOTAL_CELLS = GRID_ROWS * GRID_COLS;
  const MASK_SIZE =
    dimension === ShareImageType.INSTAGRAM_POST
      ? 108
      : dimension === ShareImageType.INSTAGRAM_STORY
        ? 120
        : 100;

  // Calculate cell dimensions
  const cellWidth = canvas.width / GRID_COLS;
  const cellHeight = MASK_SIZE; // Use mask size as cell height to remove gaps

  // Draw images in grid
  for (let i = 0; i < TOTAL_CELLS; i++) {
    const row = Math.floor(i / GRID_COLS);
    const col = i % GRID_COLS;
    const imageIndex = i % artistImages.length; // Repeat images if needed

    // Calculate position for the mask
    const maskX = col * cellWidth + (cellWidth - MASK_SIZE) / 2; // Center horizontally
    const maskY = row * MASK_SIZE; // Start from top, no gaps between rows

    // Create square mask
    ctx.save();
    ctx.beginPath();
    ctx.rect(maskX, maskY, MASK_SIZE, MASK_SIZE);
    ctx.clip();

    // Draw image maintaining aspect ratio and filling the square
    const image = artistImages[imageIndex];
    const aspectRatio = image.width / image.height;

    // Calculate dimensions to fit the square while maintaining aspect ratio
    let drawWidth, drawHeight, drawX, drawY;

    if (aspectRatio > 1) {
      // Image is wider than tall
      drawHeight = MASK_SIZE;
      drawWidth = drawHeight * aspectRatio;
      drawX = maskX - (drawWidth - MASK_SIZE) / 2;
      drawY = maskY;
    } else {
      // Image is taller than wide
      drawWidth = MASK_SIZE;
      drawHeight = drawWidth / aspectRatio;
      drawX = maskX;
      drawY = maskY - (drawHeight - MASK_SIZE) / 2;
    }

    // Draw image normally
    ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
    ctx.restore();
  }

  // Apply blur effect to the entire background if buttons are selected
  if (showPosition || showPositionSize || showWinnings) {
    // Create a temporary canvas for the blur effect
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = canvas.width;
    tempCanvas.height = canvas.height;
    const tempCtx = tempCanvas.getContext("2d");

    if (tempCtx) {
      // Draw the current canvas content to the temporary canvas
      tempCtx.drawImage(canvas, 0, 0);

      // Apply blur effect with reduced intensity
      tempCtx.filter = "blur(6px)";
      tempCtx.drawImage(canvas, 0, 0);

      // Clear the main canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw the blurred background starting from top
      ctx.drawImage(tempCanvas, 0, 0);
    }
  }

  // Add gradient overlay
  const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
  if (dimension === ShareImageType.INSTAGRAM_POST) {
    // Instagram Post specific gradient
    gradient.addColorStop(0, "rgba(15, 23, 42, 0.00)");
    gradient.addColorStop(0.5128, "#0F172A");
  } else if (dimension === ShareImageType.INSTAGRAM_STORY) {
    // Instagram Story specific gradient
    gradient.addColorStop(0, "rgba(15, 23, 42, 0.00)");
    gradient.addColorStop(0.556, "#0F172A");
  } else {
    // Default gradient for other dimensions
    gradient.addColorStop(0, "rgba(15, 23, 42, 0.00)");
    gradient.addColorStop(0.6541, "#0F172A");
  }
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Add text at the bottom
  ctx.textAlign = "center";
  ctx.textBaseline = "bottom";

  // Draw Hypeboard name
  ctx.font =
    dimension === ShareImageType.INSTAGRAM_POST
      ? "52px PhonkRegular"
      : "52px PhonkRegular";
  ctx.fillStyle = "hsla(158, 66%, 78%, 1)";
  ctx.fillText(
    battle.hypeboard.name,
    canvas.width / 2,
    canvas.height -
      (dimension === ShareImageType.INSTAGRAM_POST
        ? 492
        : dimension === ShareImageType.INSTAGRAM_STORY
          ? 760
          : 244)
  );

  // Draw HYPEBOARD text
  ctx.font =
    dimension === ShareImageType.INSTAGRAM_POST
      ? "114px PhonkRegular"
      : dimension === ShareImageType.INSTAGRAM_STORY
        ? "114px PhonkRegular"
        : "72px PhonkRegular";
  ctx.fillStyle = "white";
  ctx.fillText(
    "HYPEBOARD",
    canvas.width / 2,
    canvas.height -
      (dimension === ShareImageType.INSTAGRAM_POST
        ? 340
        : dimension === ShareImageType.INSTAGRAM_STORY
          ? 600
          : 160)
  );

  // Draw month next to HYPEBOARD only for Twitter
  if (dimension === ShareImageType.TWITTER) {
    const month = new Date(battle.start_date * 1000)
      .toLocaleString("en-US", { month: "short" })
      .toUpperCase();
    ctx.font = "900 32px GeistVF";
    ctx.fillStyle = "white";
    ctx.fillText(
      month,
      canvas.width / 2 + 460,
      canvas.height - 172 // Fixed position for Twitter
    );
  }

  // Draw line below HYPEBOARD
  const lineWidth = 980;
  const lineHeight = 4;
  ctx.fillStyle = "#5869A3";
  ctx.fillRect(
    canvas.width / 2 - lineWidth / 2,
    canvas.height -
      (dimension === ShareImageType.INSTAGRAM_POST
        ? 306
        : dimension === ShareImageType.INSTAGRAM_STORY
          ? 560 // Moved up to make room for QR code and month
          : 150),
    lineWidth,
    lineHeight
  );

  // Add month for Instagram Post & Story
  if (
    dimension === ShareImageType.INSTAGRAM_POST ||
    dimension === ShareImageType.INSTAGRAM_STORY
  ) {
    const month = new Date(battle.start_date * 1000)
      .toLocaleString("en-US", { month: "short" })
      .toUpperCase();
    ctx.font = "900 40px GeistVF";
    ctx.fillStyle = "white";

    if (dimension === ShareImageType.INSTAGRAM_STORY) {
      // Right position for Story
      ctx.textAlign = "right";
      ctx.fillText(month, canvas.width - 80, canvas.height - 100);
    } else {
      // Right position for Post
      ctx.textAlign = "right";
      ctx.fillText(month, canvas.width - 56, canvas.height - 110);
    }
    ctx.textAlign = "center"; // Reset text alignment
  }

  // Add Dripit logo with label background
  const logoImage = await loadSvgFile(fullLogoSvg, "#0F172A");

  // Draw rounded label background
  const labelWidth =
    dimension === ShareImageType.INSTAGRAM_STORY
      ? 240
      : dimension === ShareImageType.TWITTER &&
          battle.type === BattlesType.hypeboard_battle
        ? 160 // Smaller width for Twitter Hypeboard
        : 200;
  const labelHeight =
    dimension === ShareImageType.INSTAGRAM_STORY
      ? 96
      : dimension === ShareImageType.TWITTER &&
          battle.type === BattlesType.hypeboard_battle
        ? 64 // Smaller height for Twitter Hypeboard
        : 80;

  // For Twitter Hypeboard battles, adjust positioning based on curator existence
  let labelX, labelY;
  if (
    dimension === ShareImageType.TWITTER &&
    battle.type === BattlesType.hypeboard_battle
  ) {
    if (battle.hypeboard?.curator) {
      // If curator exists, place logo on the right, 64px more left
      labelX = canvas.width - labelWidth - 40 - 64; // 40px padding from right edge, plus 64px more left
    } else {
      // If no curator, keep logo centered
      labelX = canvas.width / 2 - labelWidth / 2;
    }
    labelY = canvas.height - 116 + 8; // Move logo 8px lower for Twitter Hypeboard
  } else {
    // For other dimensions, keep original positioning
    labelX = canvas.width / 2 - labelWidth / 2;
    labelY =
      canvas.height -
      (dimension === ShareImageType.INSTAGRAM_POST
        ? 196
        : dimension === ShareImageType.INSTAGRAM_STORY
          ? 464 // Moved up to make room for QR code and month
          : 116);
  }

  // Create rounded rectangle path
  ctx.beginPath();
  ctx.moveTo(labelX + labelHeight / 2, labelY);
  ctx.lineTo(labelX + labelWidth - labelHeight / 2, labelY);
  ctx.arcTo(
    labelX + labelWidth,
    labelY,
    labelX + labelWidth,
    labelY + labelHeight / 2,
    labelHeight / 2
  );
  ctx.lineTo(labelX + labelWidth, labelY + labelHeight - labelHeight / 2);
  ctx.arcTo(
    labelX + labelWidth,
    labelY + labelHeight,
    labelX + labelWidth - labelHeight / 2,
    labelY + labelHeight,
    labelHeight / 2
  );
  ctx.lineTo(labelX + labelHeight / 2, labelY + labelHeight);
  ctx.arcTo(
    labelX,
    labelY + labelHeight,
    labelX,
    labelY + labelHeight - labelHeight / 2,
    labelHeight / 2
  );
  ctx.lineTo(labelX, labelY + labelHeight / 2);
  ctx.arcTo(labelX, labelY, labelX + labelHeight / 2, labelY, labelHeight / 2);
  ctx.closePath();

  // Fill the label background
  ctx.fillStyle = "#A2ECD1";
  ctx.fill();

  // Draw the logo with background
  ctx.drawImage(
    logoImage,
    labelX +
      (labelWidth -
        (dimension === ShareImageType.INSTAGRAM_POST
          ? 133.4
          : dimension === ShareImageType.INSTAGRAM_STORY
            ? 157.4
            : dimension === ShareImageType.TWITTER &&
                battle.type === BattlesType.hypeboard_battle
              ? 100 // Smaller logo for Twitter Hypeboard battles
              : 129.4)) /
        2,
    labelY +
      (labelHeight -
        (dimension === ShareImageType.INSTAGRAM_POST
          ? 48
          : dimension === ShareImageType.INSTAGRAM_STORY
            ? 56
            : dimension === ShareImageType.TWITTER &&
                battle.type === BattlesType.hypeboard_battle
              ? 36 // Smaller logo for Twitter Hypeboard battles
              : 44)) /
        2,
    dimension === ShareImageType.INSTAGRAM_POST
      ? 133.4
      : dimension === ShareImageType.INSTAGRAM_STORY
        ? 157.4
        : dimension === ShareImageType.TWITTER &&
            battle.type === BattlesType.hypeboard_battle
          ? 100 // Smaller logo for Twitter Hypeboard battles
          : 129.4,
    dimension === ShareImageType.INSTAGRAM_POST
      ? 48
      : dimension === ShareImageType.INSTAGRAM_STORY
        ? 56
        : dimension === ShareImageType.TWITTER &&
            battle.type === BattlesType.hypeboard_battle
          ? 36 // Smaller logo for Twitter Hypeboard battles
          : 44
  );
  ctx.filter = "none"; // Reset filter

  // Draw curator info if exists
  if (battle.hypeboard?.curator) {
    const curator = battle.hypeboard.curator;

    // For Twitter Hypeboard battles, position curator on the left
    let curatorX, curatorY;
    if (
      dimension === ShareImageType.TWITTER &&
      battle.type !== BattlesType.head2head_battle
    ) {
      curatorX = 40 + 64; // 40px padding from left edge, plus 64px more right
      curatorY = canvas.height - 116 + 40; // Same Y position as logo, plus 40px more down
    } else {
      // For other dimensions, keep original positioning
      curatorX = canvas.width / 2;
      curatorY = canvas.height / 2;
    }

    // Draw curator avatar if exists
    // if (curator.avatar) {
    //   const avatarSize = 80;
    //   const avatarX = curatorX - avatarSize / 2; // Center the avatar
    //   const avatarY = curatorY;

    //   // Draw avatar circle background with dashed border
    //   ctx.beginPath();
    //   ctx.arc(
    //     avatarX + avatarSize / 2,
    //     avatarY + avatarSize / 2,
    //     avatarSize / 2 + 1,
    //     0,
    //     Math.PI * 2
    //   );
    //   ctx.strokeStyle = "#A2ECD1";
    //   ctx.lineWidth = 2;
    //   ctx.setLineDash([2, 2]);
    //   ctx.stroke();
    //   ctx.setLineDash([]);

    //   // Draw avatar circle
    //   ctx.beginPath();
    //   ctx.arc(
    //     avatarX + avatarSize / 2,
    //     avatarY + avatarSize / 2,
    //     avatarSize / 2,
    //     0,
    //     Math.PI * 2
    //   );
    //   ctx.fillStyle = "#1E293B";
    //   ctx.fill();

    //   try {
    //     const avatarImage = await loadImage(curator.avatar);
    //     ctx.save();
    //     ctx.beginPath();
    //     ctx.arc(
    //       avatarX + avatarSize / 2,
    //       avatarY + avatarSize / 2,
    //       avatarSize / 2,
    //       0,
    //       Math.PI * 2
    //     );
    //     ctx.clip();
    //     ctx.drawImage(avatarImage, avatarX, avatarY, avatarSize, avatarSize);
    //     ctx.restore();
    //   } catch (error) {
    //     // Silently fail if avatar can't be loaded
    //   }
    // }

    // Draw curator text above avatar
    const textY = curatorY - 40; // Position text above avatar

    // Draw "Curated by" text
    // ctx.font = "24px sans-serif";
    // ctx.fillStyle = "#A2ECD1";
    // ctx.textAlign = "center";
    // ctx.textBaseline = "middle";
    // ctx.fillText("Curated by", curatorX, textY - 24);

    // Draw curator username
    // ctx.font = "bold 36px sans-serif";
    // ctx.fillStyle = "#A2ECD1";
    // ctx.fillText(curator.username, curatorX, textY + 24);
  }

  // Add QR code based on dimension
  if (showQR) {
    if (dimension === ShareImageType.TWITTER) {
      await drawQRCodeWithText(ctx, 40, 40, 100);
    } else if (dimension === ShareImageType.INSTAGRAM_POST) {
      // For Hypeboard Instagram Post, place QR at top right
      await drawQRCodeWithText(ctx, canvas.width - 180, 40, 140);
    } else if (dimension === ShareImageType.INSTAGRAM_STORY) {
      // For Hypeboard Instagram Story, place QR at bottom left
      await drawQRCodeWithText(ctx, 80, canvas.height - 280, 140);
    }
  }

  // Draw selected artist in center (after ALL other elements)
  if (
    showPosition &&
    battle.userPositions?.length > 0 &&
    battle.type === BattlesType.hypeboard_battle
  ) {
    // Get the position with the highest amount
    const highestPosition = battle.userPositions.reduce((prev, current) => {
      return Number(prev.amount) > Number(current.amount) ? prev : current;
    });

    const selectedArtist = battle.battleSides.find(
      (side) => side.id === highestPosition.battle_side_id
    )?.artist;

    if (selectedArtist?.image && selectedArtist?.name) {
      // Draw circle background with smaller size
      const centerX = canvas.width / 2;
      const centerY =
        canvas.height / 2 +
        (dimension === ShareImageType.INSTAGRAM_STORY
          ? -380 - 36 // Moved up 36px total (12px + 24px)
          : dimension === ShareImageType.INSTAGRAM_POST
            ? -280 - 36 // Moved up 36px total (12px + 24px)
            : -200);
      const circleRadius =
        dimension === ShareImageType.TWITTER
          ? 80
          : dimension === ShareImageType.INSTAGRAM_STORY
            ? 180
            : 160;

      // Add shadow
      ctx.shadowColor = "rgba(0, 0, 0, 0.3)";
      ctx.shadowBlur = 25;
      ctx.shadowOffsetX = -10;
      ctx.shadowOffsetY = 10;

      // Draw background circle for all dimensions
      ctx.beginPath();
      ctx.arc(centerX, centerY, circleRadius + 10, 0, Math.PI * 2);
      ctx.fillStyle = "#0F172A"; // slate-900
      ctx.fill();
      ctx.strokeStyle = "#A2ECD1";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Draw circle
      ctx.beginPath();
      ctx.arc(centerX, centerY, circleRadius, 0, Math.PI * 2);
      ctx.fillStyle = "#0F172A";
      ctx.fill();

      // Reset shadow
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 0;

      // Draw artist image
      try {
        const artistImage = await loadImage(selectedArtist.image);

        // Calculate dimensions to maintain aspect ratio
        const aspectRatio = artistImage.width / artistImage.height;
        let drawWidth, drawHeight;

        if (aspectRatio > 1) {
          // Image is wider than tall
          drawHeight = (circleRadius - 4) * 2;
          drawWidth = drawHeight * aspectRatio;
        } else {
          // Image is taller than wide
          drawWidth = (circleRadius - 4) * 2;
          drawHeight = drawWidth / aspectRatio;
        }

        // Calculate position to center the image
        const drawX = centerX - drawWidth / 2;
        const drawY = centerY - drawHeight / 2;

        // Draw the image
        ctx.save();
        ctx.beginPath();
        ctx.arc(centerX, centerY, circleRadius - 4, 0, Math.PI * 2);
        ctx.clip();
        ctx.drawImage(artistImage, drawX, drawY, drawWidth, drawHeight);
        ctx.restore();

        // Draw star SVG with arrow at the bottom of the circle
        const starSize = dimension === ShareImageType.TWITTER ? 72 : 96; // Doubled from 36/48 to 72/96
        const starX = centerX - starSize / 2;
        const starY = centerY + circleRadius - starSize / 2;

        // Draw star background
        ctx.beginPath();
        ctx.moveTo(
          starX + 17.0479 * (starSize / 36),
          starY + 1.81299 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 17.6609 * (starSize / 36),
          starY + 1.32344 * (starSize / 36),
          starX + 18.5541 * (starSize / 36),
          starY + 1.35872 * (starSize / 36),
          starX + 19.127 * (starSize / 36),
          starY + 1.91846 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 21.3516 * (starSize / 36),
          starY + 4.09229 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 21.3975 * (starSize / 36),
          starY + 4.13135 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 21.5096 * (starSize / 36),
          starY + 4.21475 * (starSize / 36),
          starX + 21.6559 * (starSize / 36),
          starY + 4.2414 * (starSize / 36),
          starX + 21.792 * (starSize / 36),
          starY + 4.20068 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 24.7715 * (starSize / 36),
          starY + 3.31006 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 24.9258 * (starSize / 36),
          starY + 3.27197 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 25.6963 * (starSize / 36),
          starY + 3.12303 * (starSize / 36),
          starX + 26.4716 * (starSize / 36),
          starY + 3.56945 * (starSize / 36),
          starX + 26.7188 * (starSize / 36),
          starY + 4.33154 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 27.6777 * (starSize / 36),
          starY + 7.29053 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 27.7002 * (starSize / 36),
          starY + 7.34619 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 27.7608 * (starSize / 36),
          starY + 7.47207 * (starSize / 36),
          starX + 27.8782 * (starSize / 36),
          starY + 7.56313 * (starSize / 36),
          starX + 28.0176 * (starSize / 36),
          starY + 7.59033 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 31.0703 * (starSize / 36),
          starY + 8.18604 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 31.2236 * (starSize / 36),
          starY + 8.22412 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 31.9252 * (starSize / 36),
          starY + 8.43511 * (starSize / 36),
          starX + 32.3891 * (starSize / 36),
          starY + 9.10758 * (starSize / 36),
          starX + 32.3379 * (starSize / 36),
          starY + 9.83838 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 32.3193 * (starSize / 36),
          starY + 9.99561 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 31.7939 * (starSize / 36),
          starY + 13.062 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 31.7667 * (starSize / 36),
          starY + 13.2217 * (starSize / 36),
          starX + 31.8279 * (starSize / 36),
          starY + 13.3834 * (starSize / 36),
          starX + 31.9541 * (starSize / 36),
          starY + 13.4849 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 34.3809 * (starSize / 36),
          starY + 15.4312 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 34.499 * (starSize / 36),
          starY + 15.5366 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 35.0222 * (starSize / 36),
          starY + 16.0496 * (starSize / 36),
          starX + 35.1205 * (starSize / 36),
          starY + 16.8606 * (starSize / 36),
          starX + 34.7354 * (starSize / 36),
          starY + 17.4839 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 34.6455 * (starSize / 36),
          starY + 17.6138 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 32.7559 * (starSize / 36),
          starY + 20.0845 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 32.6572 * (starSize / 36),
          starY + 20.2134 * (starSize / 36),
          starX + 32.6363 * (starSize / 36),
          starY + 20.3859 * (starSize / 36),
          starX + 32.7012 * (starSize / 36),
          starY + 20.5347 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 33.9453 * (starSize / 36),
          starY + 23.3853 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 34.001 * (starSize / 36),
          starY + 23.5337 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 34.2256 * (starSize / 36),
          starY + 24.2309 * (starSize / 36),
          starX + 33.9361 * (starSize / 36),
          starY + 24.9948 * (starSize / 36),
          starX + 33.3057 * (starSize / 36),
          starY + 25.3677 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 33.165 * (starSize / 36),
          starY + 25.4409 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 30.3438 * (starSize / 36),
          starY + 26.7505 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 30.2151 * (starSize / 36),
          starY + 26.8102 * (starSize / 36),
          starX + 30.1235 * (starSize / 36),
          starY + 26.9265 * (starSize / 36),
          starX + 30.0947 * (starSize / 36),
          starY + 27.063 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 30.0859 * (starSize / 36),
          starY + 27.1235 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 29.8623 * (starSize / 36),
          starY + 30.2251 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 29.8048 * (starSize / 36),
          starY + 31.024 * (starSize / 36),
          starX + 29.1596 * (starSize / 36),
          starY + 31.6431 * (starSize / 36),
          starX + 28.376 * (starSize / 36),
          starY + 31.6831 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 28.2168 * (starSize / 36),
          starY + 31.6831 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 25.1104 * (starSize / 36),
          starY + 31.5317 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 24.9685 * (starSize / 36),
          starY + 31.5248 * (starSize / 36),
          starX + 24.8331 * (starSize / 36),
          starY + 31.5852 * (starSize / 36),
          starX + 24.7441 * (starSize / 36),
          starY + 31.6929 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 24.709 * (starSize / 36),
          starY + 31.7417 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 23.0693 * (starSize / 36),
          starY + 34.3853 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 22.647 * (starSize / 36),
          starY + 35.0659 * (starSize / 36),
          starX + 21.7877 * (starSize / 36),
          starY + 35.3138 * (starSize / 36),
          starX + 21.0752 * (starSize / 36),
          starY + 34.9849 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 20.9346 * (starSize / 36),
          starY + 34.9116 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 18.2549 * (starSize / 36),
          starY + 33.3325 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 18.1325 * (starSize / 36),
          starY + 33.2605 * (starSize / 36),
          starX + 17.9843 * (starSize / 36),
          starY + 33.2521 * (starSize / 36),
          starX + 17.8555 * (starSize / 36),
          starY + 33.3062 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 17.8018 * (starSize / 36),
          starY + 33.3325 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 15.1211 * (starSize / 36),
          starY + 34.9116 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 14.4306 * (starSize / 36),
          starY + 35.3181 * (starSize / 36),
          starX + 13.5541 * (starSize / 36),
          starY + 35.1379 * (starSize / 36),
          starX + 13.0762 * (starSize / 36),
          starY + 34.5151 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 12.9863 * (starSize / 36),
          starY + 34.3853 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 11.3467 * (starSize / 36),
          starY + 31.7417 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 11.2718 * (starSize / 36),
          starY + 31.6212 * (starSize / 36),
          starX + 11.1449 * (starSize / 36),
          starY + 31.5447 * (starSize / 36),
          starX + 11.0059 * (starSize / 36),
          starY + 31.5327 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 10.9453 * (starSize / 36),
          starY + 31.5317 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 7.83887 * (starSize / 36),
          starY + 31.6831 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 7.03881 * (starSize / 36),
          starY + 31.7223 * (starSize / 36),
          starX + 6.34607 * (starSize / 36),
          starY + 31.1564 * (starSize / 36),
          starX + 6.21191 * (starSize / 36),
          starY + 30.3833 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 6.19336 * (starSize / 36),
          starY + 30.2251 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 5.96973 * (starSize / 36),
          starY + 27.1235 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 5.95951 * (starSize / 36),
          starY + 26.9817 * (starSize / 36),
          starX + 5.88256 * (starSize / 36),
          starY + 26.8541 * (starSize / 36),
          starX + 5.76465 * (starSize / 36),
          starY + 26.7788 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 5.71191 * (starSize / 36),
          starY + 26.7505 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 2.89062 * (starSize / 36),
          starY + 25.4409 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 2.1155 * (starSize / 36),
          starY + 25.0812 * (starSize / 36),
          starX + 1.7696 * (starSize / 36),
          starY + 24.1685 * (starSize / 36),
          starX + 2.11133 * (starSize / 36),
          starY + 23.3853 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 3.35449 * (starSize / 36),
          starY + 20.5347 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 3.375 * (starSize / 36),
          starY + 20.478 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 3.40784 * (starSize / 36),
          starY + 20.3627 * (starSize / 36),
          starX + 3.39345 * (starSize / 36),
          starY + 20.2385 * (starSize / 36),
          starX + 3.33398 * (starSize / 36),
          starY + 20.1343 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 3.2998 * (starSize / 36),
          starY + 20.0845 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 1.41016 * (starSize / 36),
          starY + 17.6138 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 0.891178 * (starSize / 36),
          starY + 16.9351 * (starSize / 36),
          starX + 1.00929 * (starSize / 36),
          starY + 15.9658 * (starSize / 36),
          starX + 1.67578 * (starSize / 36),
          starY + 15.4312 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 4.10156 * (starSize / 36),
          starY + 13.4849 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 4.14551 * (starSize / 36),
          starY + 13.4448 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 4.2282 * (starSize / 36),
          starY + 13.358 * (starSize / 36),
          starX + 4.27271 * (starSize / 36),
          starY + 13.2414 * (starSize / 36),
          starX + 4.26855 * (starSize / 36),
          starY + 13.1216 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 4.2627 * (starSize / 36),
          starY + 13.062 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 3.7373 * (starSize / 36),
          starY + 9.99561 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 3.59304 * (starSize / 36),
          starY + 9.15349 * (starSize / 36),
          starX + 4.14684 * (starSize / 36),
          starY + 8.34986 * (starSize / 36),
          starX + 4.98535 * (starSize / 36),
          starY + 8.18604 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 8.03906 * (starSize / 36),
          starY + 7.59033 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 8.09668 * (starSize / 36),
          starY + 7.57471 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 8.22883 * (starSize / 36),
          starY + 7.52979 * (starSize / 36),
          starX + 8.33404 * (starSize / 36),
          starY + 7.42544 * (starSize / 36),
          starX + 8.37793 * (starSize / 36),
          starY + 7.29053 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 9.33691 * (starSize / 36),
          starY + 4.33154 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 9.39355 * (starSize / 36),
          starY + 4.18311 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 9.70873 * (starSize / 36),
          starY + 3.4644 * (starSize / 36),
          starX + 10.5166 * (starSize / 36),
          starY + 3.08053 * (starSize / 36),
          starX + 11.2842 * (starSize / 36),
          starY + 3.31006 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 14.2637 * (starSize / 36),
          starY + 4.20068 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 14.3232 * (starSize / 36),
          starY + 4.21436 * (starSize / 36)
        );
        ctx.bezierCurveTo(
          starX + 14.4611 * (starSize / 36),
          starY + 4.23585 * (starSize / 36),
          starX + 14.6026 * (starSize / 36),
          starY + 4.19143 * (starSize / 36),
          starX + 14.7041 * (starSize / 36),
          starY + 4.09229 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 16.9287 * (starSize / 36),
          starY + 1.91846 * (starSize / 36)
        );
        ctx.lineTo(
          starX + 17.0479 * (starSize / 36),
          starY + 1.81299 * (starSize / 36)
        );
        ctx.closePath();

        // Fill and stroke the star
        ctx.fillStyle = "#0F172A";
        ctx.fill();
        ctx.strokeStyle = "#A2ECD1";
        ctx.lineWidth = 1.12621 * (starSize / 36); // Scale stroke width proportionally
        ctx.stroke();

        // Draw arrow inside star
        const arrowSize = starSize * 0.6; // Keep the same proportion but now based on larger star size
        const arrowX = centerX - arrowSize / 2;
        const arrowY =
          centerY + circleRadius - starSize / 2 + (starSize - arrowSize) / 2;

        // Draw arrow
        ctx.beginPath();
        ctx.moveTo(
          arrowX + 9.02734 * (arrowSize / 18),
          arrowY + 14.5644 * (arrowSize / 19)
        );
        ctx.lineTo(
          arrowX + 9.02734 * (arrowSize / 18),
          arrowY + 4.48438 * (arrowSize / 19)
        );
        ctx.moveTo(
          arrowX + 3.98828 * (arrowSize / 18),
          arrowY + 9.52437 * (arrowSize / 19)
        );
        ctx.lineTo(
          arrowX + 9.02828 * (arrowSize / 18),
          arrowY + 4.48438 * (arrowSize / 19)
        );
        ctx.lineTo(
          arrowX + 14.0683 * (arrowSize / 18),
          arrowY + 9.52437 * (arrowSize / 19)
        );
        ctx.strokeStyle = "#A2ECD1";
        ctx.lineWidth = 1.85143 * (arrowSize / 18); // Scale stroke width proportionally
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.stroke();

        // Draw selected artist name and position amount
        if (showPosition) {
          // Adjust textY position for Instagram dimensions
          const textY =
            centerY +
            circleRadius +
            starSize / 2 +
            (dimension === ShareImageType.INSTAGRAM_POST ||
            dimension === ShareImageType.INSTAGRAM_STORY
              ? 52 // 40 + 12px for Instagram
              : 40); // Original position for Twitter

          // Draw artist name
          ctx.font =
            dimension === ShareImageType.TWITTER
              ? "32px GeistVF"
              : "48px GeistVF"; // Larger for Instagram
          ctx.fillStyle = "#A2ECD1";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(selectedArtist.name, centerX, textY);

          // Draw position amount only if showPositionSize is true
          if (showPositionSize) {
            const positionAmount = usdcToUsd(
              Number(highestPosition.amount)
            ).toFixed(2);
            ctx.font =
              dimension === ShareImageType.TWITTER
                ? "800 24px GeistVF"
                : "800 36px GeistVF"; // Larger for Instagram
            ctx.fillStyle = "#A2ECD1";
            ctx.fillText(
              `$ ${positionAmount}`,
              centerX,
              textY + (dimension === ShareImageType.TWITTER ? 40 : 60)
            ); // Increased spacing for Instagram
          }

          // Draw winnings label if showWinnings is true
          if (showWinnings) {
            const claim = battle.claims?.find(
              (claim) => claim.position_id === highestPosition.id
            );
            if (claim) {
              const winningAmount = usdcToUsd(Number(claim.amount));
              const positionSize = highestPosition.amount;
              const profitPercentage = positionSize
                ? (winningAmount / usdcToUsd(Number(positionSize)) - 1) * 100
                : 0;

              // Draw label background
              // 10% larger for Instagram post and story
              let labelPadding = 16;
              let labelSpacing = 8;
              let labelHeight = 56;
              let labelRadius = 24; // Increased radius for all dimensions
              let winningFont = "800 32px PhonkRegular";
              let profitFont = "600 24px GeistVF";
              let labelY = centerY + circleRadius + starSize / 2 + 40; // Base position

              if (dimension === ShareImageType.TWITTER) {
                labelY = Math.round(labelY - 240); // Move 500px up for Twitter
              }

              if (
                dimension === ShareImageType.INSTAGRAM_POST ||
                dimension === ShareImageType.INSTAGRAM_STORY
              ) {
                labelPadding = Math.round(labelPadding * 1.1);
                labelSpacing = Math.round(labelSpacing * 1.1);
                labelHeight = Math.round(labelHeight * 1.1);
                labelRadius = Math.round(labelRadius * 1.1);
                winningFont = "800 35px PhonkRegular"; // 32 * 1.1 ≈ 35
                profitFont = "600 26px GeistVF"; // 24 * 1.1 ≈ 26
                labelY = Math.round(labelY - 560); // Move label 680px higher for Instagram
              }

              // Calculate text widths
              ctx.font = winningFont;
              const winningText = `$ ${winningAmount.toFixed(2)}`;
              const winningWidth = ctx.measureText(winningText).width;

              ctx.font = profitFont;
              const profitText = `+${profitPercentage.toFixed(1)}%`;
              const profitWidth = ctx.measureText(profitText).width;

              const totalWidth =
                winningWidth + profitWidth + labelPadding * 2 + labelSpacing;
              const labelX =
                dimension === ShareImageType.TWITTER
                  ? canvas.width / 2 - totalWidth / 2 + 420 // Move 500px right for Twitter
                  : canvas.width / 2 - totalWidth / 2;

              // Draw rounded rectangle background
              ctx.beginPath();
              ctx.moveTo(labelX + labelRadius, labelY);
              ctx.lineTo(labelX + totalWidth - labelRadius, labelY);
              ctx.quadraticCurveTo(
                labelX + totalWidth,
                labelY,
                labelX + totalWidth,
                labelY + labelRadius
              );
              ctx.lineTo(
                labelX + totalWidth,
                labelY + labelHeight - labelRadius
              );
              ctx.quadraticCurveTo(
                labelX + totalWidth,
                labelY + labelHeight,
                labelX + totalWidth - labelRadius,
                labelY + labelHeight
              );
              ctx.lineTo(labelX + labelRadius, labelY + labelHeight);
              ctx.quadraticCurveTo(
                labelX,
                labelY + labelHeight,
                labelX,
                labelY + labelHeight - labelRadius
              );
              ctx.lineTo(labelX, labelY + labelRadius);
              ctx.quadraticCurveTo(
                labelX,
                labelY,
                labelX + labelRadius,
                labelY
              );
              ctx.closePath();

              // Fill background
              ctx.fillStyle = "#0F172A";
              ctx.fill();

              // Draw border
              ctx.strokeStyle = "#A2ECD1";
              ctx.lineWidth = 2;
              ctx.stroke();

              // Draw winning amount
              ctx.font = winningFont;
              ctx.fillStyle = "#A2ECD1";
              ctx.textAlign = "left";
              ctx.fillText(
                winningText,
                labelX + labelPadding,
                labelY + labelHeight / 2 + 2
              );

              // Draw profit percentage
              ctx.font = profitFont;
              ctx.fillStyle = "#4ADE80";
              ctx.fillText(
                profitText,
                labelX + labelPadding + winningWidth + labelSpacing,
                labelY + labelHeight / 2 + 2
              );
            }
          }
        }
      } catch (error) {
        // Silently fail if image can't be loaded
      }
    }
  }

  // Convert to base64
  const base64Image = canvas.toDataURL("image/png");
  return base64Image;
};
